import * as THREE from 'three';
import type { Content } from '@/content/index';
import type { Case, Door, Effects, EquipAction, FloorIdT, Mission, Npc } from '@/content/schema';
import { animateDoors } from '@/world/floorBuilder';
import { bus } from './bus';
import type { CardChoice, Ctx, UIApi, WorldApi } from './context';
import { nextRandom, seedFrom } from './rng';
import { Save } from './save';
import { clamp, clone, DAY_VERSION, NEWBIE_DAYS, type DayState, type Meta, type Profile } from './state';
import { shiftRange } from './time';
import { shortCode } from './codes';
import { Stage, APRON_COLOR, type Actor } from '@/world/stage';
import { animateChibi } from '@/world/chibi';
import { distToRect } from '@/world/collision';
import { applyEffects, tickPlayer } from '@/systems/stats';
import { planCases, activeCase, talkToCaseNpc, inspectTarget } from '@/systems/cases';
import { planOrders, tickProduction, completion } from '@/systems/production';
import { tickProximity, tickTimed, showEvent, fire, eligible, scheduleNext } from '@/systems/events';
import { onZoneChange, canCross, blockedAtDoor, lockerCard, washCard, washKindOf, WASH_ORDER, supplyCard, type CrossResult } from '@/systems/hygiene';
import { patrollers, watcherFloor, watcherPos, whoSees, tickVisits } from '@/systems/watchers';
import { auditorSpotsGuaiguai, canPlaceOn, consume, grantItem, hasItem, placeGuaiguai, takeBackGuaiguai } from '@/systems/items';
import { planMissions, carrySources, canRetry, retryMission, missionsFor, runMission, tickMissions, onWashed, startByTalk, currentMission, mealCard, scold, internAllowed, supervisorNear, takeJob, missionKind, missionStep } from '@/systems/missions';
import { initFlow, tickFlow, flowUse, freightChoices, cartSummary, basketLabel, basketColor as flowBasketColor } from '@/systems/flow';
import { initCarts, cartAction, basketAction, putDown, releaseCartForStairs, carryAlong, handStack, stackFrom, basketHeldOrOnCart } from '@/systems/carry';
import { spawnGround, pickUp, handleBag } from '@/systems/pickups';
import { bump, setFlag, rollChallenges, check } from '@/systems/achievements';
import { tickRats, catchRatCard, qaRatChoices, isQa } from '@/systems/rats';
import { gainXp, gainPoints, hasPerk, unlockRecipe, unlockRandomRecipe, menuToday } from '@/systems/progress';
import { tr } from '@/i18n';
import { missingNeeds, packSources, readThermo, tempSpots, thermoText } from '@/systems/worksites';

const MOVE_KEYS = ['w', 'a', 's', 'd', 'arrowup', 'arrowdown', 'arrowleft', 'arrowright'];
type Target = { kind: 'actor' | 'equipment' | 'cart' | 'pickup' | 'basket' | 'door' | 'rat' | 'worker'; id: string; label: string; d: number };
const WEEKDAY = [tr("一"), tr("二"), tr("三"), tr("四"), tr("五"), tr("六"), tr("日")];
/** 遊戲裡的星期：第 1 天是星期一 */
export const weekdayOf = (day: number) => ((day - 1) % 7) + 1;
const REPORTERS = new Set(['qa-patroller', 'qa-manager', 'eng-tech']);
/** 看到你在生產區跑步會警告的人（品保、駐廠） */
const QA_WATCHERS = new Set(['qa-patroller', 'qa-manager', 'resident']);

/**
 * Game：把 content、存檔、系統、3D 舞台、UI 串起來的協調者。
 * 遊戲規則都在 systems/；這裡只做流程、輸入與互動分派。
 */
export class Game implements Ctx, WorldApi {
  profile: Profile;
  day!: DayState;
  meta: Meta = { rewinds: 0, checkpoints: [] };
  ui!: UIApi;
  readonly world: WorldApi = this;
  stage: Stage;
  paused = true;
  running = false;
  /** v1.2 手機虛擬搖桿（-1～1；y 往下＝往南） */
  joy: [number, number] = [0, 0];
  private keys = new Set<string>();
  private walkTo: { x: number; z: number } | null = null;
  private path: [number, number][] = [];
  private pending: { kind: Target['kind']; id: string } | null = null;
  private clock = new THREE.Clock();
  private acc = { prox: 0, save: 0, timed: 0, workers: 0 };
  private ray = new THREE.Raycaster(); private ndc = new THREE.Vector2();
  private shiftWarned = false;
  private walking = false;
  private lastDir: [number, number] = [0, 0];
  private airshowerUntil = 0;
  private goal: [number, number] | null = null;
  private repaths = 0;
  private stuck = { t: 0, d: Infinity };
  private blockedToastAt = 0;
  /** 跑步：按住 Shift，或按畫面上的「跑」切換 */
  runToggle = false; private shift = false; private isRunning = false; private runWarnAt = -99; private runWhyAt = -99; private doorHintAt = 0;
  private doorTimers = new Map<string, number>();
  private trailAt = 0;
  readonly reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;

  constructor(readonly c: Content, canvas: HTMLCanvasElement) {
    this.profile = Save.loadProfile();
    this.stage = new Stage(canvas);
    this.stage.makePlayer(this.profile.appearance, 'white', 'none', false);
    this.bindInput(canvas);
  }
  attachUI(ui: UIApi) { this.ui = ui; }

  // ================================================================== Ctx
  save(): void { if (this.day) Save.saveDay(this.day, this.meta); Save.saveProfile(this.profile); }
  log(text: string, kind: DayState['log'][number]['kind'] = 'info'): void { this.day.log.push({ at: this.day.clock, text, kind }); if (this.day.log.length > 150) this.day.log.shift(); }
  apply(e: Effects | undefined, _source: string): void { applyEffects(this.day, e); bus.emit('stateChanged', undefined); }
  advance(minutes: number, _reason?: string): void {
    if (!this.day || minutes <= 0) return;
    const d = this.day;
    d.clock += minutes;
    tickPlayer(d, minutes, this.c.game.player);
    const z = this.rt()?.zoneAt(...this.playerPos());
    if (z?.cold) { d.player.fatigue = clamp(d.player.fatigue + minutes * (z.cold === 'frozen' ? 0.5 : 0.2)); }
    tickProduction(this, minutes);
    tickFlow(this, minutes);
    const elapsed = (d.clock - d.shiftStart) / Math.max(1, d.shiftEnd - d.shiftStart);
    if (elapsed > 0.2 && completion(this) < elapsed * 0.6) d.player.stress = clamp(d.player.stress + minutes * 0.05);
    const gg = d.items.guaiguai;
    if (gg && !d.items.spotted && gg.floor !== d.floor && patrollers(this).some(n => watcherFloor(this, n) === gg.floor) && nextRandom(d) < 0.004 * minutes) d.flags.guaiguaiPending = true;
    if (!this.shiftWarned && d.clock >= d.shiftEnd) { this.shiftWarned = true; this.ui.toast(tr("🔔 下班時間到了！到 1F 打卡鐘打卡。")); bus.emit('sfx', 'ok'); }
    if (d.clock >= d.shiftEnd + 240 && !d.ended) { this.log(tr("超過下班時間太久，被主管趕回家"), 'bad'); this.ui.ending(); }
    bus.emit('stateChanged', undefined);
  }
  checkpoint(label: string, reopenEvent?: string): void {
    if (!this.day) return;
    const max = hasItem(this, 'charm-senior') ? 5 : 4;
    const state = clone({ ...this.day, pos: this.playerPos() });
    const code = shortCode();
    this.meta.checkpoints.push({ label, state, reopenEvent, code });
    while (this.meta.checkpoints.length > max) this.meta.checkpoints.shift();
    Save.saveSnapshot(code, state);
    this.profile.codes[code] = { label, day: state.day, clock: state.clock, role: state.roleId, savedAt: Date.now() };
    const keys = Object.entries(this.profile.codes).sort((a, b) => b[1].savedAt - a[1].savedAt).map(([k]) => k);
    for (const k of keys.slice(30)) { delete this.profile.codes[k]; try { localStorage.removeItem(`ffg:code:${k}`); } catch { /* noop */ } }
  }
  rewind(): void {
    const cp = this.meta.checkpoints[this.meta.checkpoints.length - 1];
    if (!cp) { this.ui.toast(tr("沒有可以回去的時間點了。")); return; }
    const free = this.day.items.held.includes('charm-today') && !this.day.items.freeRewindUsed;
    this.day = clone(cp.state);
    this.meta.rewinds++; bump(this, 'rewinds');
    if (free) { consume(this, 'charm-today'); this.day.items.freeRewindUsed = true; this.ui.toast(tr("🧧 平安符化成灰……這次不扣專注。")); }
    else if (!hasPerk(this, 15)) this.day.player.focus = clamp(this.day.player.focus - this.c.game.rewind.focusCost);
    bus.emit('sfx', 'rewind');
    this.ui.close();
    this.applyLook();
    this.changeFloor(this.day.floor, this.day.pos);
    this.log(tr("「我要重新活一次」→ 回到：{0}", cp.label), 'warn');
    this.save();
    document.body.classList.add('rewinding'); setTimeout(() => document.body.classList.remove('rewinding'), 900);
    const ev = cp.reopenEvent ? this.c.eventById.get(cp.reopenEvent) : null;
    if (ev) {
      this.day.events.last[ev.id] = this.day.clock;
      if (!this.day.events.firedToday.includes(ev.id)) this.day.events.firedToday.push(ev.id);
      setTimeout(() => showEvent(this, ev), 700);
    } else this.ui.toast(tr("⏪ 時間倒轉：{0}", cp.label));
  }
  /** 用進度代碼（或快照）載入一個時間點 */
  loadSnapshot(state: DayState, label: string): void {
    this.day = clone(state);
    this.meta = { rewinds: 0, checkpoints: [] };
    this.shiftWarned = this.day.clock >= this.day.shiftEnd;
    bump(this, 'codeLoads');
    rollChallenges(this);
    this.applyLook();
    this.changeFloor(this.day.floor, this.day.pos);
    this.checkpoint(tr("代碼讀取：{0}", label));
    this.save();
  }

  // ================================================================== WorldApi
  rt() { return this.stage.rt; }
  playerPos(): [number, number] { return [this.stage.player.position.x, this.stage.player.position.z]; }
  setPlayerPos(x: number, z: number) { this.stage.player.position.set(x, 0, z); }
  moveDir(): [number, number] { return this.walking ? this.lastDir : [0, 0]; }
  airshower(seconds: number): void { this.airshowerUntil = this.clock.elapsedTime + seconds; this.keys.clear(); this.stuck = { t: 0, d: Infinity }; }
  pushBack(from: [number, number], dist: number) {
    const p = this.stage.player.position; const dx = p.x - from[0], dz = p.z - from[1], l = Math.hypot(dx, dz) || 1;
    const nx = p.x + dx / l * dist, nz = p.z + dz / l * dist;
    if (this.rt().walkable(nx, nz)) p.set(nx, 0, nz);
    this.walkTo = null; this.path = []; this.pending = null; this.keys.clear();
  }
  private doorOk = (d: Door) => d.type !== 'hidden' || !!this.profile.flags[d.flag ?? ''];
  changeFloor(id: FloorIdT, pos?: [number, number]): void {
    const f = this.c.floorById.get(id)!;
    let target = pos ?? f.spawn;
    this.day.floor = id;
    this.day.pack ??= []; this.day.inProd ??= !!this.day.washed;      // 舊進度代碼（v0.4）沒有這兩個欄位
    this.day.earn ??= { xp: 0, points: 0, raise: 0, notes: [] }; this.day.rat ??= null; this.day.shelfTaken ??= {}; this.day.chatted ??= [];
    this.stage.loadFloor(f, target, { ...this.profile.flags, ...this.day.flags }, !!this.day.flags.lights5F, this.doorOk);
    // v0.5：如果抵達點（樓梯口、舊存檔的位置）卡在牆或設備裡，就移到最近的空地，不會卡住動不了
    if (!this.rt().walkable(target[0], target[1])) { target = this.nearestFree(target, f.spawn); this.setPlayerPos(target[0], target[1]); }
    this.day.zone = this.rt().zoneAt(target[0], target[1])?.id ?? '';
    if (!this.day.floorsVisited.includes(id)) { this.day.floorsVisited.push(id); check(this); }
    this.walkTo = null; this.path = []; this.pending = null; this.doorTimers.clear();
    carryAlong(this);
    this.refreshActors();
    bus.emit('floorChanged', id);
    if (f.dark && !this.day.flags.lights5F) this.day.flags.dark5F = true;
    if (this.day.flags.guaiguaiPending && this.day.items.guaiguai?.floor === id) { this.day.flags.guaiguaiPending = false; setTimeout(() => auditorSpotsGuaiguai(this), 400); }
    if (f.dark && !this.day.flags.lights5F) setTimeout(() => this.ui.toast(hasItem(this, 'flashlight') ? tr("🔦 打開手電筒。開關應該在樓梯口附近。") : tr("好暗……只看得到腳邊。開關應該在樓梯口附近——或回 4F 跟廠務春美借手電筒。")), 300);
  }
  rebuildFloor(): void { this.changeFloor(this.day.floor, this.playerPos()); }
  randomOpenIn(rect: [number, number, number, number]): [number, number] | null { return this.stage.nav.randomOpenIn(rect, Math.random); }
  /** 從 (x,z) 往外一圈一圈找可以站的地方（同一個區域優先） */
  nearestFree(p: [number, number], fallback: [number, number]): [number, number] {
    const rt = this.rt(); const z0 = rt.zoneAt(p[0], p[1])?.id;
    let any: [number, number] | null = null;
    for (let r = 0.4; r <= 6; r += 0.4) for (let a = 0; a < 16; a++) {
      const x = p[0] + Math.cos(a / 16 * Math.PI * 2) * r, z = p[1] + Math.sin(a / 16 * Math.PI * 2) * r;
      if (!rt.walkable(x, z, 0.4)) continue;
      if (rt.zoneAt(x, z)?.id === z0) return [x, z];
      any ??= [x, z];
    }
    return any ?? fallback;
  }
  applyLook(): void {
    const role = this.c.roleById.get(this.day?.roleId ?? '') ;
    // 新人：帽子頭頂有黃色圈圈，工作滿三個月（遊戲裡 90 天）就拿掉
    const hat = role?.hat === 'none' && this.profile.day <= NEWBIE_DAYS ? 'yellow' : role?.hat ?? 'none';
    this.stage.makePlayer(this.profile.appearance, role?.uniform === 'office' ? 'white' : role?.uniform ?? 'white', hat, !!this.day?.dressed, role?.apron ? APRON_COLOR[role.apron] : undefined);
  }
  refreshActors(): void {
    const st = this.stage, d = this.day, rt = st.rt, f = rt.floor;
    st.removeActors(() => true);
    f.workers.forEach((w, i) => st.addWorker(w, i));
    d.caseQueue.forEach((id, i) => {
      const k = this.c.caseById.get(id)!; if (k.floor !== d.floor) return;
      const pr = d.cases[id]; if (i > d.activeCase && pr.stage === 'pending') return;
      st.addNpc({ id: `case:${id}`, kind: 'caseNpc', caseId: id, npc: this.c.npcById.get(k.npc)!, pos: this.npcSpot(k) });
    });
    for (const n of this.c.npcs) if (n.floor === d.floor && n.pos && !n.patrol && this.npcOnDuty(n)) st.addNpc({ id: n.id, kind: 'ambient', npc: n, pos: n.pos });
    st.setRat(d.rat && d.rat.floor === d.floor ? d.rat.pos : null);
    for (const n of patrollers(this)) if (watcherFloor(this, n) === d.floor) this.spawnPatrol(n);
    const gg = d.items.guaiguai;
    if (gg && gg.floor === d.floor) { const e = rt.equipment.get(gg.equipment); if (e) st.setGuaiguai([e.data.pos[0] + e.data.size[0] / 2 - 0.25, e.data.size[1], e.data.pos[1] + e.data.size[2] / 2 - 0.15]); }
    else st.setGuaiguai(null);
    if (hasItem(this, 'spare-tag')) {
      const pts: [number, number][] = [];
      for (const ev of this.c.events) if (ev.tier === 'D') for (const a of ev.trigger.anchors) if (a.floor === d.floor) { const an = rt.anchor(a.target); if (an) pts.push([an.x, an.z]); }
      st.setHotspots(pts);
    } else st.setHotspots([]);
    st.syncCarts(d.carts, d.floor, d.pushing, this.basketColor, d.holding?.kind === 'cart' ? d.holding.id : null);
    st.syncBaskets(d.baskets, d.floor, this.contentColor);
    st.setHolding(d.holding?.kind ?? null, d.holding?.kind === 'basket' ? handStack(d).map(this.basketColor) : undefined);
    st.syncPickups(d.ground, d.floor, t => { const p = this.c.pickupById.get(t)!; return { emoji: p.icon, image: p.image }; });
    st.flashlight = hasItem(this, 'flashlight');
    this.applyLook();
    bus.emit('stateChanged', undefined);
  }
  private contentColor = (content: string) => flowBasketColor(this.c, content);
  private basketColor = (id: string) => this.contentColor(this.day.baskets[id]?.content ?? '');
  private npcSpot(k: Case): [number, number] {
    if (k.npcPos) return k.npcPos;
    const rt = this.rt(); const e = rt.equipment.get(k.equipment) ?? rt.doors.get(k.equipment);
    const [ex, ez] = e ? e.data.pos : [0, 0];
    const size = e && 'size' in e.data ? e.data.size : [1, 1, 1];
    const cands: [number, number][] = [
      [ex + 1.4, ez + size[2] / 2 + 1.0], [ex - 1.4, ez + size[2] / 2 + 1.0], [ex, ez + size[2] / 2 + 1.3],
      [ex + size[0] / 2 + 1.0, ez], [ex - size[0] / 2 - 1.0, ez], [ex, ez - size[2] / 2 - 1.2], [ex + 2, ez + 2], [ex - 2, ez + 2],
    ];
    const z0 = rt.zoneAt(ex, ez + size[2] / 2 + 0.6);
    const free = (x: number, z: number) => rt.walkable(x, z, 0.5) && !rt.floor.workers.some(w => Math.hypot(w.pos[0] - x, w.pos[1] - z) < 0.8);
    return cands.find(([x, z]) => free(x, z) && (!z0 || rt.zoneAt(x, z)?.id === z0.id)) ?? cands.find(([x, z]) => free(x, z)) ?? [ex, ez + 2];
  }
  private spawnPatrol(n: Npc): void {
    const zones = this.rt().floor.zones.filter(z => z.level >= 0 && !z.cold);
    const z = zones[Math.floor(Math.random() * zones.length)] ?? this.rt().floor.zones[0];
    const p = this.stage.nav.randomOpenIn(z.rect, Math.random) ?? this.rt().floor.spawn;
    const a = this.stage.addNpc({ id: `patrol:${n.id}`, kind: 'patrol', npc: n, sub: n.id === 'resident' ? tr("外部・巡查中") : tr("巡查中"), pos: p });
    a.speed = 1.25; a.path = [];
    watcherPos.set(n.id, { x: p[0], z: p[1], floor: this.day.floor });
  }

  // ================================================================== 一天
  newDay(roleId: string, opts: { mode?: 'work' | 'intern'; tutorial?: boolean } = {}): void {
    const role = this.c.roleById.get(roleId)!;
    const [s, e] = shiftRange(role.shift.start, role.shift.end);
    this.profile.roleId = roleId;
    this.meta = { rewinds: 0, checkpoints: [] };
    this.shiftWarned = false;
    const gate = this.c.floorById.get('1F')!;    // v0.4：每個人都從 1F 廠區外的大門、打卡鐘開始
    this.day = {
      version: DAY_VERSION, day: this.profile.day, roleId, rng: seedFrom(this.profile.day * 7919 + roleId.length * 131 + Date.now() % 100000),
      clock: s - 5, shiftStart: s, shiftEnd: e, floor: '1F', pos: gate.spawn, zone: '',
      player: { hp: this.c.game.player.hpMax, fatigue: 8, hydration: 90, focus: 100, stress: 20 },
      factory: { foodSafety: 85, records: 70, trust: 60, audit: 100, fines: 0, caught: 0, unseen: 0, reworkMinutes: 0 },
      injuries: [], caseQueue: [], activeCase: 0, cases: {}, missionQueue: [], missions: {}, missionStartedAt: {}, orders: { official: false, lines: [] }, lines: {},
      events: { last: {}, firedToday: [], ignored: 0, inside: [], playSec: 0, nextRandomAt: 0 },
      items: { held: [], guaiguai: null, spotted: false, freeRewindUsed: false },
      dressed: false, washed: false, clockedIn: false, carts: {}, pushing: null, holding: null, baskets: {}, bag: [], ground: [], floorsVisited: [], pack: [], inProd: false, earn: { xp: 0, points: 0, raise: 0, notes: [] }, rat: null, shelfTaken: {}, chatted: [],
      log: [], flags: {}, ended: false,
      mode: opts.mode ?? 'work', tutorial: !!opts.tutorial,
    };
    planCases(this); planMissions(this); planOrders(this); initCarts(this); initFlow(this); spawnGround(this); scheduleNext(this, true);
    // v0.6：連續上班、等級能力
    const P = this.profile;
    P.streak = P.counters.lastWorkDay === P.day - 1 ? (P.streak ?? 0) + 1 : 1; P.counters.lastWorkDay = P.day;
    if (hasPerk(this, 5)) this.day.factory.trust = clamp(this.day.factory.trust + 5);
    if (P.stash?.['rat-radar-today']) { this.day.flags.ratRadar = true; delete P.stash['rat-radar-today']; }
    for (const k of role.knowledge) if (!this.profile.knowledge.includes(k)) this.profile.knowledge.push(k);
    if (!this.profile.deptsPlayed.includes(role.dept)) this.profile.deptsPlayed.push(role.dept);
    rollChallenges(this, () => nextRandom(this.day));
    this.log(tr("第 {0} 天｜{1}・{2}（{3} {4}–{5}）到廠", this.day.day, role.deptName, role.name, role.shift.label, role.shift.start, role.shift.end), 'info');
    if (this.day.mode === 'intern') this.log(tr("自由模式：今天是「{0}」的實習生。沒有主線任務，到處看看、接支線；但不要亂跑出自己部門的區域。", role.deptName), 'info');
    this.changeFloor('1F', gate.spawn);
    this.checkpoint(tr("到廠（大門口）"));
    check(this);
    this.save();
  }
  resumeDay(): boolean {
    const s = Save.loadDay(); if (!s || s.day.ended) return false;
    if (s.day.caseQueue.some(id => !this.c.caseById.has(id)) || !this.c.floorById.has(s.day.floor) || !this.c.roleById.has(s.day.roleId)) { Save.clearDay(); return false; }
    this.day = s.day; this.meta = s.meta; this.shiftWarned = this.day.clock >= this.day.shiftEnd;
    this.changeFloor(this.day.floor, this.day.pos);
    return true;
  }
  start(): void { this.running = true; this.paused = false; this.clock.getDelta(); }
  stop(): void { this.paused = true; }
  endDay(): void {
    if (this.day.ended) return;
    gainXp(this, 'dayEnd');
    if ((this.profile.streak ?? 0) >= 3) gainPoints(this, 'streak', tr("連續上班 {0} 天", this.profile.streak));
    this.day.ended = true; this.paused = true; bump(this, 'daysEnded'); this.save();
  }
  /** 有排班的 NPC（廠醫：每週三 14:00～16:00）才會出現 */
  npcOnDuty(n: Npc): boolean {
    const sc = n.schedule; if (!sc) return true;
    if (weekdayOf(this.day.day) !== sc.weekday) return false;
    const [h1, m1] = sc.from.split(':').map(Number), [h2, m2] = sc.to.split(':').map(Number);
    const t = ((this.day.clock % 1440) + 1440) % 1440;
    return t >= h1 * 60 + m1 && t < h2 * 60 + m2;
  }
  nextDay(): void { this.profile.day++; Save.clearDay(); Save.saveProfile(this.profile); }

  // ================================================================== 互動
  nearest(): Target | null {
    const [x, z] = this.playerPos();
    let best: Target | null = null;
    const cands: Target[] = [];
    const consider = (t: Target) => { cands.push(t); if (!best || t.d < best.d) best = t; };
    const k = this.day ? activeCase(this) : null;
    const stg = k ? this.day.cases[k.id].stage : null;
    const target = stg === 'inspect' ? k!.equipment : null;
    const holding = this.day?.holding;
    if (this.day?.rat && this.day.rat.floor === this.day.floor) {
      const dd = Math.hypot(this.day.rat.pos[0] - x, this.day.rat.pos[1] - z);
      if (dd < 1.6) consider({ kind: 'rat', id: 'rat', label: tr("🐀 抓老鼠！"), d: dd - 1 });
    }
    for (const a of this.stage.actors) {
      if (a.kind === 'worker') {
        const raw = Math.hypot(a.group.position.x - x, a.group.position.z - z);
        if (raw < 1.5) consider({ kind: 'worker', id: a.id, label: this.day?.chatted?.includes(a.id) ? tr("💬 再聊兩句") : tr("💬 和作業員聊聊"), d: raw + 0.45 });
        continue;
      }
      const raw = Math.hypot(a.group.position.x - x, a.group.position.z - z);
      if (raw > 1.9) continue;
      const npc = a.npcId ? this.c.npcById.get(a.npcId) : null;
      const hasTask = npc && missionsFor(this, `npc:${npc.id}`).length > 0;
      const bonus = (k && a.caseId === k.id && (stg === 'talk' || stg === 'report')) || hasTask ? 1.2 : 0;
      consider({ kind: 'actor', id: a.id, label: tr("和{0}說話{1}", npc?.shortName ?? tr("他"), hasTask ? tr("（任務）") : ''), d: raw - bonus });
    }
    if (this.day) {
      for (const [id, c] of Object.entries(this.day.carts)) {
        if (c.floor !== this.day.floor || id === this.day.pushing || holding?.id === id) continue;
        const dd = Math.hypot(c.pos[0] - x, c.pos[1] - z);
        const n = (c.load?.baskets ?? 0) + c.baskets.length;
        if (dd < 1.3) consider({ kind: 'cart', id, label: holding?.kind === 'basket' ? tr("🧺 把籃子放上烏龜車") : this.day.pushing ? tr("🛒 這台烏龜車：{0}", cartSummary(this, id)) : n ? tr("烏龜車：{0}", cartSummary(this, id)) : tr("烏龜車：推／拿起"), d: dd + (holding?.kind === 'basket' ? -0.6 : 0.2) });
      }
      if (this.day.pushing) consider({ kind: 'cart', id: this.day.pushing, label: tr("放開烏龜車"), d: 1.75 });
      for (const [id, b] of Object.entries(this.day.baskets)) {
        if (b.floor !== this.day.floor || b.on !== null || holding?.kind === 'cart') continue;
        const stack = stackFrom(this.day, id); const free = stack.filter(s => !this.day.baskets[s].used);
        if (!free.length && holding?.kind !== 'basket') continue;
        const dd = Math.hypot(b.pos[0] - x, b.pos[1] - z);
        const top = free.length ? this.day.baskets[free[free.length - 1]] : null;
        const name = top ? basketLabel(this.c, top.content) : tr("籃子");
        const label = holding?.kind === 'basket' ? tr("⬆️ 把手上的籃子疊上去（這疊 {0} 籃）", stack.length) : this.day.pushing ? tr("🧺 放上推車：{0}", name) : stack.length > 1 ? tr("🧺 拿起：{0}（這疊 {1} 籃）", name, stack.length) : tr("🧺 拿起：{0}", name);
        if (dd < 1.1) consider({ kind: 'basket', id, label, d: dd - (holding ? 0.4 : 0.2) });
      }
      for (const g of this.day.ground) {
        if (g.floor !== this.day.floor || holding) continue;
        const dd = Math.hypot(g.pos[0] - x, g.pos[1] - z);
        if (dd < 1.1) consider({ kind: 'pickup', id: g.uid, label: tr("撿起：{0}", this.c.pickupById.get(g.type)!.name), d: dd - 0.3 });
      }
      for (const dr of this.rt().doors.values()) {
        if (!dr.solid) continue;
        const dd = distToRect(x, z, dr.box);
        if (dd < 0.9) consider({ kind: 'door', id: dr.data.id, label: dr.open ? tr("關上{0}", dr.data.slide ? tr("拉門") : tr("門")) : `${dr.data.slide ? tr("拉開") : tr("推開")}${dr.data.name.split(tr("（"))[0].trim()}`, d: dd + (dr.open ? 0.6 : -0.1) });
      }
    }
    for (const e of this.rt().equipment.values()) {
      if (e.data.kind === 'floorZone') continue;
      const d = distToRect(x, z, e.box);
      if (d > 1.05) continue;
      const task = this.day && missionsFor(this, e.data.id).length > 0;
      const bonus = e.data.id === target ? 1.5 : task ? 1.0 : 0;
      consider({ kind: 'equipment', id: e.data.id, label: this.equipLabel(e.data.id, !!task), d: d + 0.3 - bonus });
    }
    // v1.2 旁邊有好幾個可以互動的東西：可以切換（手機 ⇄ 鍵、電腦 Tab）
    cands.sort((a, b) => a.d - b.d);
    this.cands = cands.filter((t, i) => cands.findIndex(u => u.kind === t.kind && u.id === t.id) === i);
    const picked = this.pickTarget ? this.cands.find(t => `${t.kind}:${t.id}` === this.pickTarget) : null;
    if (this.pickTarget && !picked) this.pickTarget = null;
    return picked ?? best;
  }
  private cands: Target[] = [];
  private pickTarget: string | null = null;
  /** 旁邊可以互動的東西有幾個 */
  targetCount(): number { return this.cands.length; }
  /** 換下一個互動對象 */
  cycleTarget(): Target | null {
    const cur = this.nearest(); if (this.cands.length < 2 || !cur) return cur;
    const i = this.cands.findIndex(t => t.kind === cur.kind && t.id === cur.id);
    const nx = this.cands[(i + 1) % this.cands.length];
    this.pickTarget = `${nx.kind}:${nx.id}`; bus.emit('stateChanged', undefined); return nx;
  }
  private equipLabel(id: string, task: boolean): string {
    const e = this.rt().equipment.get(id)!.data; const k = activeCase(this);
    if (k && k.equipment === id && this.day.cases[k.id].stage === 'inspect') return tr("檢查 {0}", e.name);
    if (task) return tr("任務：{0}", e.name.split(tr("（"))[0].trim());
    if (this.day?.holding?.kind === 'basket') return tr("🧺 把籃子放到 {0} 旁", e.name.split(tr("（"))[0].trim());
    switch (e.interaction?.type) {
      case 'stairs': return e.interaction.lift ? tr("🛗 搭電梯") : tr("走樓梯"); case 'freight': return tr("🛗 {0}（物料專用）", e.name); case 'clinic': return id === 'clinic-bed' ? tr("躺一下") : id === 'clinic-scale' ? tr("量體重") : tr("醫務室"); case 'shop': return tr("🎟️ 福利社"); case 'clock': return this.day?.clockedIn ? tr("打卡下班") : tr("⏰ 打卡上班"); case 'rest': return tr("喝水・上廁所"); case 'meal': return this.day?.flags.ateLunch ? tr("員工餐廳（吃過了）") : tr("🍱 吃午餐");
      case 'orders': return tr("看訂單看板"); case 'item': return tr("翻找 {0}", e.name); case 'locker': return this.day?.dressed ? tr("更衣室") : tr("換工作服");
      case 'sink': case 'wash': { const k = washKindOf(e.kind); return this.day?.washed ? tr("手已洗好") : k === 'sink' ? tr("🧼 洗手") : k === 'sprayer' ? tr("🧴 酒精消毒") : tr("💨 烘手"); } case 'bin': return tr("丟垃圾"); case 'light': return this.day?.flags.lights5F ? tr("關燈") : tr("開燈");
      case 'supply': return tr("拿手套・圍裙"); case 'board': return tr("📌 看公告"); case 'lend': return tr("借東西");
      case 'doc': return tr("翻文件"); case 'slide': return tr("看看這面牆"); case 'shelf': return tr("看貨架"); case 'drawer': return tr("開抽屜");
      case 'thermo': return this.day?.temps?.[e.interaction.spot] != null ? tr("🌡️ {0}°C（看過了）", this.day.temps[e.interaction.spot]) : tr("🌡️ 看溫度計");
    }
    return tr("查看 {0}", e.name.split(tr("（"))[0].trim());
  }
  interact(target: Target | null = this.nearest()): void {
    if (this.ui.isOpen() || this.clock.elapsedTime < this.airshowerUntil) return;
    if (!target) { if (this.day?.holding) putDown(this, null); return; }
    this.walkTo = null; this.path = []; this.pending = null; this.keys.clear();
    bus.emit('sfx', 'click');
    if (target.kind === 'actor') return this.talk(target.id);
    if (target.kind === 'cart') return cartAction(this, target.id);
    if (target.kind === 'basket') return basketAction(this, target.id);
    if (target.kind === 'pickup') return pickUp(this, target.id);
    if (target.kind === 'rat') return catchRatCard(this);
    if (target.kind === 'worker') return this.chatWorker(target.id);
    if (target.kind === 'door') return this.toggleDoor(target.id);
    this.useEquipment(target.id);
  }
  /** 手動門：推開／拉開；幾秒後自動關上（人在門口時不關） */
  toggleDoor(id: string, open?: boolean): void {
    const dr = this.rt().doors.get(id); if (!dr?.solid) return;
    if (dr.data.auto) { this.ui.toast(tr("🚪 這是自動門：走過去就會開。另一道開著的時候不會開。")); return; }
    const want = open ?? !dr.open;
    if (!want && this.inDoorway(dr.solid)) { this.ui.toast(tr("先走出門口再關門。")); return; }
    this.rt().setDoorOpen(id, want); bus.emit('sfx', 'door');
    if (want) { this.doorTimers.set(id, this.clock.elapsedTime + this.c.game.hygiene.doorOpenSeconds); bump(this, 'doorsOpened');
      if (dr.data.id === 'door-1f-dock-outer' && this.rt().doors.get('door-1f-dock-inner')?.open) this.ui.toast(tr("🦟 內外兩道拉門同時開著，蚊蟲會飛進來！先關一道。"));
      else if (dr.data.id === 'door-1f-dock-inner' && this.rt().doors.get('door-1f-dock-outer')?.open) this.ui.toast(tr("🦟 內外兩道拉門同時開著，蚊蟲會飛進來！先關一道。"));
      else if (dr.data.cold) this.ui.toast(tr("{0}開{1}。進去前看一下門內的釋放裝置和求救鈴。", dr.data.slide ? tr("拉") : tr("推"), dr.data.name.split(tr("（"))[0].trim()));
    } else this.doorTimers.delete(id);
  }
  private inDoorway(r: { minX: number; maxX: number; minZ: number; maxZ: number }): boolean {
    const [x, z] = this.playerPos(); return x > r.minX - 0.45 && x < r.maxX + 0.45 && z > r.minZ - 0.45 && z < r.maxZ + 0.45;
  }
  private talk(id: string): void {
    if (id.startsWith('case:')) return talkToCaseNpc(this, this.c.caseById.get(id.slice(5))!);
    const npcId = id.startsWith('patrol:') ? id.slice(7) : id;
    const npc = this.c.npcById.get(npcId); if (!npc) return;
    const task = missionsFor(this, `npc:${npc.id}`)[0];
    if (task) { runMission(this, task, { npc: npc.id }); return; }
    const started = startByTalk(this, npc.id);
    const choices: CardChoice[] = [];
    const lines = npc.lines ?? {};
    const pickLine = (k: string) => { const v = lines[k]; return Array.isArray(v) ? v[Math.floor(Math.random() * v.length)] : v; };
    let text = pickLine('greeting') ?? npc.catchphrase;
    const sections: { label: string; items: string[]; tone?: 'good' | 'info' }[] = [];
    if (started.length) sections.push({ label: tr("🗺️ 新的支線任務"), items: started.map(m => tr("{0} {1}：{2}", m.icon, m.title, m.summary)), tone: 'info' });
    if (npc.id === 'planner') text = (this.day.orders.official ? lines.afterOfficial : lines.beforeOfficial) as string ?? text;
    if (npc.id === 'wh-manager' && !this.profile.flags.knowKeyDrawer) choices.push({ text: tr("請問化學品儲藏室的鑰匙在哪裡？"), fn: () => {
      setFlag(this, 'knowKeyDrawer');
      this.ui.card({ name: npc.name, role: npc.title, color: npc.color, npc: npc.id, text: lines.key as string, choices: [{ text: tr("謝謝課長"), fn: () => this.ui.close() }] });
    } });
    const foreign = this.day.bag.filter(t => this.c.pickupById.get(t)!.action === 'report');
    if (foreign.length && REPORTERS.has(npc.id)) choices.push({ text: tr("交出撿到的異物（{0}），說明在哪裡撿到", foreign.map(t => this.c.pickupById.get(t)!.name).join(tr("、"))), tone: 'good', fn: () => {
      const n = handleBag(this, 'report');
      this.ui.card({ name: npc.name, role: npc.title, color: npc.color, npc: npc.id, expression: 'happy', text: (lines.report as string) ?? tr("謝謝你交過來，我會追查來源並評估受影響的產品。"),
        sections: [{ label: tr("結果"), items: [tr("交出 {0} 件異物，食安 +3、紀錄 +2", n)], tone: 'good' }], choices: [{ text: tr("好"), fn: () => this.ui.close() }] });
    } });
    if (npc.knowledge?.length) {
      const fresh = npc.knowledge.filter(k => !this.profile.knowledge.includes(k));
      if (!this.profile.cadresTalked.includes(npc.id)) { this.profile.cadresTalked.push(npc.id); check(this); }
      if (fresh.length) {
        this.profile.knowledge.push(...fresh);
        sections.push({ label: tr("📖 解鎖成品流程"), items: fresh.map(k => DEPT_NAME[k] ?? k), tone: 'good' });
      }
    }
    if (isQa(npc.id)) choices.push(...qaRatChoices(this, npc.name));
    if (npc.id === 'rd-chef' && !this.day.flags.rdRecipeToday) choices.push({ text: tr("📖 主廚，今天可以教我一道菜嗎？"), tone: 'good', fn: () => {
      this.day.flags.rdRecipeToday = true;
      const id = unlockRandomRecipe(this, tr("RD 主廚教的"));
      const m = id ? this.c.menuById.get(id)! : null;
      this.ui.card({ name: npc.name, role: npc.title, color: npc.color, npc: npc.id, expression: 'happy',
        text: m ? tr("今天教你「{0}」。組成和做法都寫在手冊的「食譜收集」裡，半成品的每一站要問那一站的幹部才會懂喔。", m.name) : tr("你已經把所有食譜都學會了！要不要來 RD 上班？"),
        choices: [{ text: tr("謝謝主廚！"), tone: 'primary', fn: () => this.ui.close() }] });
    } });
    if (npc.id === 'factory-doctor') choices.push({ text: tr("🩺 請廠醫看一下"), tone: 'good', fn: () => this.clinicCard('doctor') });
    choices.push({ text: tr("💡 聊聊工廠小知識"), fn: () => this.tipCard(npc.name, npc.title, npc.color, npc.id) });
    if (npc.id === 'planner') choices.push({ text: tr("看訂單看板"), fn: () => { this.ui.close(); this.ui.orders(); } });
    choices.push({ text: tr("好"), fn: () => this.ui.close() });
    this.ui.card({ name: npc.name, role: npc.title, color: npc.color, npc: npc.id, text, sections, choices });
  }
  private skipJobOffer = false;
  private useEquipment(id: string): void {
    const e = this.rt().equipment.get(id)!.data;
    const k = activeCase(this);
    if (k && k.equipment === id && this.day.cases[k.id].stage === 'inspect') return inspectTarget(this, k);
    const task = missionsFor(this, id)[0];
    if (task && runMission(this, task)) return;
    // v1.0 這個工作站有「作業」可以接（別的職務的＝支線；自己的＝主線）
    if (!this.skipJobOffer) {
      const here = (m: Mission) => m.steps.some(st => st.do === 'minigame' && st.target === id && st.floor === this.day.floor);
      const retry = this.c.missions.filter(m => here(m) && canRetry(this, m.id));
      // v1.1.2 選項固定三個：🎮 接下（或再挑戰）／原本的功能／不做任何事
      const orig: CardChoice = { text: tr("原本的功能"), fn: () => { this.ui.close(); this.skipJobOffer = true; try { this.useEquipment(id); } finally { this.skipJobOffer = false; } } };
      const none: CardChoice = { text: tr("不做任何事"), fn: () => this.ui.close() };
      const kindName = (m: Mission) => missionKind(this, m) === 'side' ? tr("支線") : tr("主線");
      if (retry.length) {
        const m = retry[0];
        return this.ui.card({ name: e.name, role: tr("🔁 失敗的任務"), color: '#e07b39', text: tr("{0} {1}：今天失敗了，可以再挑戰一次（每個任務一天一次）。", m.icon, m.title),
          choices: [{ text: tr("🔁 再挑戰「{0}」的任務（{1}）", m.title, kindName(m)), tone: 'primary', fn: () => { this.ui.close(); retryMission(this, m.id, id); } }, orig, none] });
      }
      const jobs = this.c.missions.filter(m => !(m.id in this.day.missions) && here(m));
      if (jobs.length) {
        // 按「接下」：第一步就在這台 → 直接進作業視窗
        return this.ui.card({ name: e.name, role: tr("🎮 這裡有工作可以做"), color: '#3f9e6c',
          text: jobs.map(m => tr("{0} {1}：{2}", m.icon, m.title, m.summary)).join('\n'),
          more: tr("接下的工作會加進任務欄（不是你職務的工作算支線）。下班前，自己的工作還是要做完。"),
          choices: [...jobs.map(m => ({ text: tr("🎮 接下「{0}」的任務（{1}）", m.title, kindName(m)), tone: 'primary' as const, fn: () => { this.ui.close(); takeJob(this, m, id); this.refreshActors(); } })), orig, none] });
      }
      // 已經在任務欄、但還沒輪到這台（前面還有步驟）：告訴玩家先做哪一步
      const queued = this.c.missions.find(m => here(m) && m.id in this.day.missions && !this.day.missions[m.id].done);
      if (queued) {
        const st = missionStep(this, queued.id);
        if (st && !(st.do === 'minigame' && st.target === id)) {
          return this.ui.card({ name: e.name, role: tr("🎮 這裡有工作可以做"), color: '#3f9e6c',
            text: tr("{0} {1}：先完成前一步——{2}・{3}", queued.icon, queued.title, st.floor, st.say),
            choices: [{ text: tr("🎯 跟著「{0}」的任務走", queued.title), tone: 'primary', fn: () => { this.day.focus = queued.id; this.ui.close(); this.updateSigns(); } }, orig, none] });
        }
      }
    }
    if (flowUse(this, id)) return;
    if (this.day.holding) return putDown(this, e);
    const it = e.interaction;
    const info = (text: string, extra: CardChoice[] = []) => this.ui.card({ name: e.name, role: this.rt().zoneAt(e.pos[0], e.pos[1] + e.size[2] / 2 + 0.5)?.name ?? '', color: '#f2b134', text, choices: [...extra, { text: tr("好"), fn: () => this.ui.close() }] });
    switch (it?.type) {
      case 'stairs': return this.ui.stairs(id);
      case 'orders': return this.ui.orders();
      case 'rest': return this.rest();
      case 'meal': mealCard(this); return;
      case 'clock': return this.clockCard();
      case 'locker': return lockerCard(this);
      case 'sink': case 'wash': return washCard(this, washKindOf(e.kind), () => onWashed(this));
      case 'supply': return supplyCard(this);
      case 'board': { const b = this.c.boardById.get(it.board)!; if (it.board === 'board-2f') this.day.flags.readBoard2F = true; bump(this, 'boardsRead');
        return this.ui.card({ name: `📌 ${b.title}`, role: tr("新品公告・班表"), color: '#8b6a3d', text: tr("生產幹部貼的最新公告："), sections: b.posts.map(p => ({ label: tr("{0}（{1}）", p.title, p.by), items: [p.text] })), choices: [{ text: tr("看完了"), tone: 'primary', fn: () => this.ui.close() }] }); }
      case 'lend': if (!hasItem(this, it.item)) { grantItem(this, it.item); return; } return info(tr("已經借過了。"));
      case 'freight': {
        const cart = this.day.pushing ? this.day.carts[this.day.pushing] : null;
        const has = cart && (cart.load || cart.baskets.length);
        const grp = it.group;
        const sameLift = (q: { interaction?: { type: string; group?: string } }) => q.interaction?.type === 'freight' && (q.interaction as { group?: string }).group === grp;
        const extra: CardChoice[] = [...freightChoices(this, id), ...(has ? this.c.floors.filter(f => f.id !== this.day.floor && f.equipment.some(sameLift)).map(f => ({ text: tr("把物料送到 {0}", f.id), fn: () => {
          const fr = f.equipment.find(sameLift)!; cart!.floor = f.id; cart!.pos = [fr.pos[0] + 0.2, fr.pos[1] + fr.size[2] / 2 + 1.1]; this.day.pushing = null;
          for (const b of cart!.baskets) this.day.baskets[b].floor = f.id;
          this.ui.close(); this.refreshActors(); this.ui.toast(tr("🛗 物料送上 {0}。人走樓梯。", f.id)); this.save();
        } })) : [])];
        const stops = this.c.floors.filter(f => f.equipment.some(sameLift)).map(f => f.id).join(tr("、"));
        return info(tr("{0}（停 {1}）：只給食材、物料、籃子用。人員請走樓梯或搭「非食品生產部門電梯」。\n{2}{3}", e.name, stops, e.info ?? '', has ? '' : tr("\n（推著裝好料的烏龜車過來，就可以送上下樓）")), extra);
      }
      case 'bin': { const n = handleBag(this, 'bin'); if (n) this.ui.toast(tr("🗑️ 丟掉 {0} 件（落地食材已記錄報廢）。", n)); else info(tr("垃圾桶。手上沒有要丟的東西。")); return; }
      case 'light': this.day.flags.lights5F = !this.day.flags.lights5F; if (this.day.flags.lights5F) setFlag(this, 'lights5F'); this.stage.setDark(!this.day.flags.lights5F, this.rt().floor); bus.emit('sfx', 'click'); this.ui.toast(this.day.flags.lights5F ? tr("💡 啪。……灰塵好多。") : tr("燈關了。")); return;
      case 'doc': setFlag(this, 'readIso'); return info(e.info ?? '', []);
      case 'slide':
        // v1.1 暗門：拉開後要記得拉回去、公告恢復原狀；開著被品保看到會記錄、罰款
        if (this.day.flags[it.flag]) return info(tr("拉門現在是開著的（公告牆滑到旁邊）。用完要拉回去，讓牆恢復原狀——開著被品保看到會被記錄、罰款。"), [{ text: tr("↩️ 把拉門拉回去、公告恢復原狀"), tone: 'primary', fn: () => { this.ui.close(); this.day.flags[it.flag] = false; bump(this, 'hiddenRestored'); this.ui.toast(tr("🧱 拉門關好了，牆上的舊公告也恢復原狀。")); this.rebuildFloor(); this.save(); } }]);
        return info(e.info ?? '', [{ text: tr("把舊公告推開"), tone: 'primary', fn: () => { this.ui.close(); this.day.flags[it.flag] = true; setFlag(this, 'found5F'); this.ui.toast(tr("🚪 是一扇拉門！後面有一道很窄的樓梯往上。用完記得拉回去恢復原狀。")); this.rebuildFloor(); this.save(); } }]);
      case 'shelf': if (e.stock?.length) { this.ui.shelfView(id); return; } return info(`${e.info ?? ''}${it.material ? tr("\n架上：{0}", it.material) : ''}${this.rt().zoneAt(e.pos[0], e.pos[1])?.cold ? tr("\n（冷到手會痛）") : ''}`);
      case 'clinic': return this.clinicCard(id);
      case 'thermo': { const v = readThermo(this, e)!; bus.emit('sfx', 'click'); bump(this, 'thermoRead'); this.save(); return info(thermoText(e, v)); }
      case 'shop': this.ui.shop(); return;
      case 'drawer': {
        if (hasItem(this, it.item)) return info(tr("抽屜裡的東西你已經拿了。"));
        if (!this.profile.flags[it.flag]) return info(tr("抽屜鎖著。鑰匙的事，先去問倉儲課長阿忠。"));
        this.advance(1, tr("開抽屜")); grantItem(this, it.item); setFlag(this, 'hasChemKey'); return;
      }
      case 'item':
        if (!hasItem(this, it.item) && !(it.item === 'charm-today' && this.day.items.freeRewindUsed)) { this.advance(2, tr("翻找")); grantItem(this, it.item); return; }
    }
    if (id === 'mystery-box') { const ev = this.c.eventById.get('SHORT-009'); if (ev && eligible(this, ev)) return fire(this, ev); }
    if (id.startsWith('label-rack') && this.day.bag.includes('label-roll')) { handleBag(this, 'label'); return info(tr("把撿到的標籤卷交回標籤架，核對版本後歸位。")); }
    if (id.startsWith('basket-washer') && this.day.bag.includes('basket-lid')) { handleBag(this, 'basket'); return info(tr("籃子蓋送回洗籃區，清洗後才會再用。")); }
    const gg = this.day.items.guaiguai;
    const choices: CardChoice[] = [];
    if (hasItem(this, 'guaiguai') && !gg && canPlaceOn(e.kind) && !this.day.items.spotted)
      choices.push({ text: tr("🟩 把綠色乖乖放在機台旁（非食品接觸位置）"), fn: () => { this.ui.close(); placeGuaiguai(this, this.day.floor, id); } });
    if (gg && gg.equipment === id) choices.push({ text: tr("把綠色乖乖收回個人物品區"), fn: () => { this.ui.close(); takeBackGuaiguai(this); } });
    // v1.1 機台的其他選項：安全的會學到東西；不安全的會工傷
    const EA = this.c.equipActions;
    for (const a of [...(EA.byId[id] ?? []), ...(EA.byKind[e.kind] ?? [])].slice(0, 3)) choices.push({ text: a.text, fn: () => this.equipAction(e.name, a) });
    const tierD = this.c.events.filter(ev => ev.tier === 'D' && ev.trigger.anchors.some(a => a.target === id));
    this.ui.card({ name: e.name, role: this.rt().zoneAt(e.pos[0], e.pos[1] + e.size[2] / 2 + 0.5)?.name ?? '', color: '#f2b134',
      text: e.info ?? tr("看起來運作正常。"),
      sections: hasItem(this, 'spare-tag') && tierD.length ? [{ label: tr("🏷️ 備品吊牌：維修紀錄"), items: tierD.map(ev => tr("{0}：{1}", ev.title, ev.triggerText)), tone: 'bad' }] : undefined,
      choices: [...choices, { text: tr("好"), fn: () => this.ui.close() }] });
  }
  /** v1.1 機台的其他選項 */
  private equipAction(name: string, a: EquipAction): void {
    this.ui.close();
    if (a.effects) this.apply(a.effects, 'equip');
    if (a.safe || !a.injury) {
      bump(this, 'equipSafe');
      this.ui.card({ name, role: tr("✔ 安全的做法"), color: '#3f9e6c', expression: 'happy', badge: { text: tr("✔ 好判斷"), color: '#3f9e6c' }, text: a.result, choices: [{ text: tr("好"), tone: 'primary', fn: () => this.ui.close() }] });
      return;
    }
    const inj = a.injury;
    this.day.injuries.push({ label: inj.label, severity: inj.severity, at: this.day.clock, source: 'equip' });
    this.day.player.hp = Math.max(1, this.day.player.hp - inj.hp);
    this.day.player.stress = Math.min(100, this.day.player.stress + 12);
    bump(this, 'workInjury'); bus.emit('sfx', 'alarm');
    this.log(tr("工傷：{0}（{1}）", inj.label, name), 'bad');
    this.ui.card({ name, role: tr("⚠️ 工傷"), color: '#d9534a', expression: 'worried', badge: { text: tr("🩹 {0}", inj.label), color: '#d9534a' }, text: a.result,
      sections: [{ label: tr("現在要做什麼"), items: [tr("馬上通報主管，到 3F 醫務室處理（床邊、辦公桌按 E）。"), tr("也可以說「{0}」回到事故前。", this.c.game.rewind.phrase)], tone: 'bad' }],
      choices: [{ text: tr("好，馬上去醫務室"), tone: 'primary', fn: () => this.ui.close() }] });
    this.save();
  }
  /** 走樓梯：抵達時出現在「同一座樓梯」（group 相同）；暗梯只通頂樓 */
  useStairs(to: FloorIdT, fromStairs?: string): void {
    releaseCartForStairs(this);
    const from = this.day.floor;
    const order = (f: FloorIdT) => this.c.floorById.get(f)!.order;
    this.advance(Math.max(1, Math.abs(order(to) - order(from))) * 1.2, tr("樓梯"));
    const f = this.c.floorById.get(to)!;
    const here = fromStairs ? this.rt().equipment.get(fromStairs)?.data : null;
    const group = here?.interaction?.type === 'stairs' ? here.interaction.group : 'main';
    const links = f.equipment.filter(e => e.interaction?.type === 'stairs' && e.interaction.to.includes(from));
    const st = links.find(e => e.interaction?.type === 'stairs' && e.interaction.group === group) ?? links[0];
    const pos: [number, number] = st ? [st.pos[0], st.pos[1] + st.size[2] / 2 + 0.9] : f.spawn;
    if (st && st.interaction?.type === 'stairs' && st.interaction.hidden) { pos[0] = st.pos[0]; pos[1] = st.pos[1] - st.size[2] / 2 - 0.8; }
    if (st && !this.c.floorById.get(to)!.zones.some(z => pos[0] >= z.rect[0] && pos[0] <= z.rect[0] + z.rect[2] && pos[1] >= z.rect[1] && pos[1] <= z.rect[1] + z.rect[3])) pos[1] = st.pos[1] - st.size[2] / 2 - 0.9;
    bus.emit('sfx', 'door');
    this.changeFloor(to, pos);
    this.checkpoint(tr("{0} 樓梯口", to));
    this.save();
    this.ui.toast(tr("{0}・{1}", to, f.name));
  }
  private rest(): void {
    const P = this.c.game.player;
    this.ui.card({ name: tr("飲水機・廁所"), role: tr("休息"), color: '#6fa8c9',
      text: tr("休息 {0} 分鐘：喝水、上廁所。疲勞 −{1}，水分補滿。\n連續式包裝線大家只能在換線時休息——但不喝水、憋尿，最後停下來的會是人。", P.restMinutes, P.restFatigueRecover),
      choices: [
        { text: tr("休息 {0} 分鐘", P.restMinutes), tone: 'primary', fn: () => {
          this.advance(P.restMinutes, tr("休息"));
          this.day.player.fatigue = clamp(this.day.player.fatigue - P.restFatigueRecover);
          this.day.player.hydration = 100; this.day.player.stress = clamp(this.day.player.stress - 5);
          this.log(tr("喝水、上廁所"), 'good'); this.ui.close(); this.ui.toast(tr("💧 補充水分完成")); this.save();
        } },
        { text: tr("先不用"), fn: () => this.ui.close() },
      ] });
  }
  /** 1F 廠區外的打卡鐘：上班打卡／下班打卡 */
  private clockCard(): void {
    const d = this.day;
    if (!d.clockedIn) {
      d.clockedIn = true; bump(this, 'clockIns'); bus.emit('sfx', 'ok');
      const role = this.c.roleById.get(d.roleId)!;
      const late = d.clock > d.shiftStart + 5;
      this.log(tr("上班打卡 {0}", late ? tr("（遲到）") : ''), late ? 'warn' : 'good');
      this.checkpoint(tr("上班打卡"));
      this.ui.card({ name: tr("⏰ 打卡鐘"), role: tr("1F 廠區外・員工大門"), color: '#d6a23c', text: tr("嗶！打卡完成。今天在 {0}。", role.startFloor), foldSections: true,
        sections: [{ label: tr("進生產區的順序"), items: [tr("① 更衣室換工作服、口罩、髮帽、雨鞋"), tr("② 氣浴室（轉一圈吹風）"), tr("③ 洗手台 → 酒精消毒機 → 烘手機"), tr("④ 推開門進生產區")], tone: 'info' },
          { label: tr("不用換裝的地方"), items: [tr("大廳、辦公室、倉儲辦公室・出貨區、進貨區、B1 常溫倉、4F 行政區")] }],
        choices: [{ text: tr("出發！"), tone: 'primary', fn: () => this.ui.close() }] });
      this.save(); return;
    }
    const left = d.caseQueue.filter(id => d.cases[id].stage !== 'done').length + d.missionQueue.filter(id => ['main', 'daily'].includes(this.c.missionById.get(id)?.type ?? '') && !d.missions[id].done).length;
    const early = d.clock < d.shiftEnd - 15;
    const warn = [left ? tr("還有 {0} 件案件或任務沒做完", left) : '', early ? tr("還沒到下班時間（早退）") : ''].filter(Boolean);
    this.ui.card({ name: tr("⏰ 打卡鐘"), role: tr("1F 廠區外"), color: '#d6a23c',
      text: warn.length ? tr("確定要打卡下班嗎？{0}。", warn.join(tr("，"))) : tr("今天辛苦了。確定要打卡下班？"),
      choices: [{ text: tr("打卡下班"), tone: warn.length ? 'danger' : 'primary', fn: () => {
        this.ui.close();
        // v1.0 自己的工作（每日、主線）沒做完就下班 → 被罵（實習生不用）
        const mine = d.missionQueue.filter(id => { const m = this.c.missionById.get(id); return m && !d.missions[id].done && ['main', 'daily'].includes(missionKind(this, m)); });
        if (mine.length && d.mode !== 'intern') {
          const names = mine.slice(0, 3).map(id => this.c.missionById.get(id)!.title).join(tr("、"));
          scold(this, tr("等一下！你自己的工作還沒做完就要走？「{0}」誰要幫你收尾？下次做完、交接好再打卡！", names), '1F', () => this.ui.ending());
          return;
        }
        this.ui.ending();
      } }, { text: tr("還沒"), fn: () => this.ui.close() }] });
  }

  // ================================================================== 輸入
  private bindInput(canvas: HTMLCanvasElement): void {
    addEventListener('keydown', e => {
      const k = e.key.toLowerCase();
      if (!this.running || this.paused || this.ui?.isOpen()) return;
      const tag = document.activeElement?.tagName ?? '';
      if (tag === 'TEXTAREA' || tag === 'SELECT' || (tag === 'INPUT' && (document.activeElement as HTMLInputElement).type !== 'checkbox')) return;   // v0.9 打字中（回饋表單）
      let onBtn = tag === 'BUTTON' || tag === 'INPUT';
      // v1.1 剛點過 HUD 上的按鈕（走／跑、訂單…）焦點還在按鈕上：鍵盤照樣控制角色（以前 R、WASD 會失效）
      if (onBtn && (document.activeElement as HTMLElement).closest('#hud')) { (document.activeElement as HTMLElement).blur(); onBtn = false; }
      if (onBtn && (k === ' ' || k === 'enter' || k.length === 1)) return;
      if (k === 'shift') this.shift = true;
      if (k === 'r' && !e.repeat) { this.runToggle = !this.runToggle; this.ui.toast(this.runToggle ? tr("🏃 跑步模式（生產區被品保看到會被警告）") : tr("🚶 走路模式")); bus.emit('stateChanged', undefined); return; }
      if (k === 'h' && !e.repeat && e.shiftKey === false && this.day) { /* H 給手冊用 */ }
      if (k === 'e' || k === ' ') { this.interact(); e.preventDefault(); e.stopImmediatePropagation(); return; }
      if (k === 'tab' && this.targetCount() > 1) { this.cycleTarget(); e.preventDefault(); return; }
      if (MOVE_KEYS.includes(k)) { this.keys.add(k); this.walkTo = null; this.path = []; this.pending = null; e.preventDefault(); }
    });
    addEventListener('keyup', e => { const k = e.key.toLowerCase(); this.keys.delete(k); if (k === 'shift') this.shift = false; });
    addEventListener('blur', () => { this.keys.clear(); this.shift = false; });
    canvas.addEventListener('wheel', e => { this.stage.zoom = Math.max(0.55, Math.min(1.7, this.stage.zoom * (e.deltaY > 0 ? 1.08 : 0.93))); e.preventDefault(); }, { passive: false });
    addEventListener('keydown', e => {
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.tagName ?? '')) return;
      if (e.key === '+' || e.key === '=') this.stage.zoom = Math.max(0.55, this.stage.zoom * 0.9);
      if (e.key === '-' || e.key === '_') this.stage.zoom = Math.min(1.7, this.stage.zoom * 1.1);
    });
    canvas.addEventListener('pointerdown', e => {
      if (!this.running || this.paused || this.ui.isOpen()) return;
      const r = canvas.getBoundingClientRect();
      this.ndc.set(((e.clientX - r.left) / r.width) * 2 - 1, -((e.clientY - r.top) / r.height) * 2 + 1);
      this.ray.setFromCamera(this.ndc, this.stage.camera);
      for (const h of this.ray.intersectObjects(this.stage.scene.children, true)) {
        const ud = h.object.userData;
        if (ud.kind === 'actor' || ud.kind === 'equipment' || ud.kind === 'cart' || ud.kind === 'pickup' || ud.kind === 'basket' || ud.kind === 'door') {
          const near = this.nearest();
          if (near && near.id === ud.id) { this.interact(near); return; }
          const p = this.approachPoint(ud.kind, ud.id); if (!p) continue;
          this.goTo(p.x, p.z); this.pending = { kind: ud.kind, id: ud.id }; return;
        }
        if (ud.kind === 'floor') {
          this.goTo(h.point.x, h.point.z); this.pending = null;
          this.stage.tapRing.position.set(h.point.x, 0.03, h.point.z); (this.stage.tapRing.material as THREE.MeshBasicMaterial).opacity = 1;
          return;
        }
      }
    });
  }
  private goTo(x: number, z: number, avoidActors = false): void {
    const nav = this.stage.nav; const from = this.playerPos();
    const goal = nav.nearestOpen(x, z);
    if (!avoidActors) this.repaths = 0;
    this.goal = [x, z];
    const avoid = avoidActors ? Object.entries(this.day.carts).filter(([id, c]) => c.floor === this.day.floor && id !== this.day.pushing).map(([, c]) => c.pos as [number, number]) : [];   // v1.0.2：人不擋路，重新規劃時只繞開烏龜車
    if (this.day) for (const [id, c] of Object.entries(this.day.carts)) if (id !== this.day.pushing && c.floor === this.day.floor) avoid.push([c.pos[0], c.pos[1]]);
    const path = goal ? nav.find(from, goal, true, avoid) : null;
    if (path && path.length) { this.path = path; this.path[this.path.length - 1] = goal!; const [px, pz] = this.path.shift()!; this.walkTo = { x: px, z: pz }; }
    else {
      this.path = []; this.walkTo = { x, z };
      if (goal && nav.find(from, goal, false)) this.ui.toast(tr("那裡要照衛生動線走：從出口退出，再從更衣室、氣浴室進來。"));
    }
  }
  private approachPoint(kind: string, id: string): { x: number; z: number } | null {
    const [px, pz] = this.playerPos();
    if (kind === 'actor') {
      const a = this.stage.actors.find(x => x.id === id); if (!a) return null;
      const dx = px - a.group.position.x, dz = pz - a.group.position.z, d = Math.hypot(dx, dz) || 1;
      return { x: a.group.position.x + dx / d * 1.3, z: a.group.position.z + dz / d * 1.3 };
    }
    if (kind === 'cart') { const c = this.day.carts[id]; return c ? { x: c.pos[0], z: c.pos[1] + 1 } : null; }
    if (kind === 'pickup') { const g = this.day.ground.find(x => x.uid === id); return g ? { x: g.pos[0], z: g.pos[1] } : null; }
    if (kind === 'basket') { const b = this.day.baskets[id]; return b ? { x: b.pos[0], z: b.pos[1] + 0.7 } : null; }
    if (kind === 'door') { const d = this.rt().doors.get(id); if (!d) return null; const off = d.axis === 'x' ? [0, Math.sign(pz - d.data.pos[1]) || 1] : [Math.sign(px - d.data.pos[0]) || 1, 0]; return { x: d.data.pos[0] + off[0] * 1.1, z: d.data.pos[1] + off[1] * 1.1 }; }
    const e = this.rt().equipment.get(id); if (!e) return null;
    const b = e.box;
    return { x: Math.max(b.minX - 0.6, Math.min(px, b.maxX + 0.6)), z: Math.max(b.minZ - 0.6, Math.min(pz, b.maxZ + 0.6)) };
  }

  // ================================================================== 主迴圈
  private cross: Extract<CrossResult, { ok: true }> | null = null;
  private blocked(x: number, z: number): boolean {
    const rt = this.rt();
    if (!rt.walkable(x, z)) {
      // 被關著的手動門擋住：點地板走路時自動推開；用鍵盤時提示按 E
      if (rt.walkable(x, z, 0.35, true)) for (const d of rt.doors.values()) {
        if (!d.solid || d.open || d.data.auto || !(x > d.solid.minX - 0.4 && x < d.solid.maxX + 0.4 && z > d.solid.minZ - 0.4 && z < d.solid.maxZ + 0.4)) continue;
        if (this.walkTo) { this.toggleDoor(d.data.id, true); this.ui.toast(tr("🚪 {0}開{1}", d.data.slide ? tr("拉") : tr("推"), d.data.name.split(tr("（"))[0].trim())); }
        else if (this.clock.elapsedTime - this.doorHintAt > 2) { this.doorHintAt = this.clock.elapsedTime; this.ui.toast(tr("🚪 門關著——按 E {0}開", d.data.slide ? tr("拉") : tr("推"))); }
      }
      return true;
    }
    // v1.0.2：人（作業員、NPC、巡場）不會擋路——擦身而過；停著的烏龜車會擋，但卡住超過 0.35 秒就側身擠過去
    if (!this.squeeze) for (const [id, c] of Object.entries(this.day.carts)) if (id !== this.day.pushing && this.day.holding?.id !== id && c.floor === this.day.floor && Math.hypot(x - c.pos[0], z - c.pos[1]) < 0.58) { this.cartHit = true; return true; }
    const cur = rt.zoneAt(...this.playerPos()), nz = rt.zoneAt(x, z);
    if (cur && nz && cur.id !== nz.id) {
      const r = canCross(this, cur, nz, x, z);
      if (!r.ok) {
        const now = this.clock.elapsedTime;
        if (now - this.blockedToastAt > 1.5) { this.blockedToastAt = now; blockedAtDoor(this, r); }
        return true;
      }
      this.cross = r;
    }
    if (this.day.pushing) {
      const [fx, fz] = this.lastDir; const cx = x + fx * 0.95, cz = z + fz * 0.95;
      if (!rt.walkable(cx, cz, 0.3)) return true;
    }
    return false;
  }
  private squeeze = false; private cartHit = false; private pressT = 0;
  /** v1.0.2 走路順暢：直走被擋住時，自動沿著障礙物的邊滑過去（轉角、機台角、烏龜車） */
  private stepMove(p: { x: number; z: number }, dx: number, dz: number, sp: number, dt: number): boolean {
    this.cartHit = false;
    let moved = false;
    if (Math.abs(dx) > 1e-3 && !this.blocked(p.x + dx * sp, p.z)) { p.x += dx * sp; moved = true; }
    if (Math.abs(dz) > 1e-3 && !this.blocked(p.x, p.z + dz * sp)) { p.z += dz * sp; moved = true; }
    if (moved) { this.pressT = 0; return true; }
    for (const deg of [35, -35, 65, -65]) {
      const r = deg * Math.PI / 180, ax = dx * Math.cos(r) - dz * Math.sin(r), az = dx * Math.sin(r) + dz * Math.cos(r);
      if (!this.blocked(p.x + ax * sp * 0.8, p.z + az * sp * 0.8)) { p.x += ax * sp * 0.8; p.z += az * sp * 0.8; this.pressT = 0; return true; }
    }
    // 被烏龜車擋住、繞不過去：一直往前走就側身擠過去
    if (this.cartHit) {
      this.pressT += dt;
      if (this.pressT > 0.35) { this.squeeze = true; const ok = !this.blocked(p.x + dx * sp, p.z + dz * sp); if (ok) { p.x += dx * sp; p.z += dz * sp; } this.squeeze = false; return ok; }
    }
    return false;
  }
  update(): void {
    const dt = Math.min(this.clock.getDelta(), 0.05), t = this.clock.elapsedTime;
    const st = this.stage; if (!st.rt) return;
    const active = this.running && !this.paused && !this.ui.isOpen();
    const inShower = t < this.airshowerUntil;
    if (active) {
      this.day.events.playSec += dt;
      this.advance(dt * this.c.game.time.gameMinutesPerRealSecond, 'time');
      let dx = 0, dz = 0;
      if (!inShower) {
        if (this.keys.has('w') || this.keys.has('arrowup')) dz -= 1;
        if (this.keys.has('s') || this.keys.has('arrowdown')) dz += 1;
        if (this.keys.has('a') || this.keys.has('arrowleft')) dx -= 1;
        if (this.keys.has('d') || this.keys.has('arrowright')) dx += 1;
        if (this.joy[0] || this.joy[1]) { dx += this.joy[0]; dz += this.joy[1]; if (this.walkTo || this.pending) { this.walkTo = null; this.path = []; this.pending = null; } }
        if (!dx && !dz && this.walkTo) {
          const vx = this.walkTo.x - st.player.position.x, vz = this.walkTo.z - st.player.position.z, d = Math.hypot(vx, vz);
          if (d < 0.12) {
            if (this.path.length) { const [nx, nz] = this.path.shift()!; this.walkTo = { x: nx, z: nz }; }
            else { this.walkTo = null; this.arrive(); }
          } else { dx = vx / d; dz = vz / d; }
        }
      }
      if (dx || dz) {
        const l = Math.hypot(dx, dz); dx /= l; dz /= l;
        this.lastDir = [dx, dz];
        const tired = this.day.player.fatigue > 75 ? 0.75 : 1;
        const cart = this.day.pushing ? this.c.game.carry.cartSpeed : this.day.holding ? 0.85 : 1;
        const M = this.c.game.move;
        const want = this.shift || this.runToggle;
        const run = want && !this.day.pushing && !this.day.holding && this.day.player.fatigue < 90;
        // v1.1 想跑卻跑不動：說明原因（以前不會講，看起來像跑步壞了）
        if (want && !run && t - this.runWhyAt > 6) {
          this.runWhyAt = t;
          this.ui.toast(this.day.pushing ? tr("🚶 推著烏龜車不能跑（會撞到人、翻車）。") : this.day.holding ? tr("🚶 手上拿著東西不能跑（會跌倒、打翻）。") : tr("🥵 太累了跑不動（疲勞 90 以上）。去喝水・上廁所的地方休息一下。"));
        }
        this.isRunning = run;
        if (run) this.day.player.fatigue = clamp(this.day.player.fatigue + M.runFatiguePerSec * dt);
        const sp = (run ? M.runSpeed : M.walkSpeed) * dt * tired * cart, p = st.player.position;
        const prevZone = this.rt().zoneAt(p.x, p.z);
        let moved = false;
        this.cross = null;
        moved = this.stepMove(p, dx, dz, sp, dt);
        if (this.walkTo) {
          const dd = Math.hypot(this.walkTo.x - p.x, this.walkTo.z - p.z);
          if (dd > this.stuck.d - 0.005) this.stuck.t += dt; else this.stuck.t = 0;
          this.stuck.d = dd;
          if (this.stuck.t > 0.5) { moved = false; this.stuck = { t: 0, d: Infinity }; }
        }
        if (!moved && this.walkTo) {
          if (this.goal && this.repaths < 2) { this.repaths++; const p0 = this.pending; this.goTo(this.goal[0], this.goal[1], true); this.pending = p0; }
          else { this.walkTo = null; this.path = []; this.arrive(); }
        }
        st.player.rotation.y = Math.atan2(dx, dz);
        this.walking = moved;
        if (this.pending) { const n = this.nearest(); if (n && n.id === this.pending.id) { this.pending = null; this.walkTo = null; this.path = []; this.interact(n); } }
        const z = this.rt().zoneAt(p.x, p.z);
        if (z && z.id !== this.day.zone) {
          const prev = this.rt().floor.zones.find(x => x.id === this.day.zone) ?? prevZone;
          this.day.zone = z.id; onZoneChange(this, prev, z, this.cross); bus.emit('stateChanged', undefined);
        }
        if (this.day.pushing) { const c = this.day.carts[this.day.pushing]; c.pos = [p.x + dx * 0.95, p.z + dz * 0.95]; c.floor = this.day.floor; }
      } else { this.walking = false; this.isRunning = false; }

      // 手動門：時間到自動關（有人站在門口就等一下）
      for (const [id, until] of this.doorTimers) {
        if (t < until) continue;
        const dr = this.rt().doors.get(id);
        if (!dr?.solid) { this.doorTimers.delete(id); continue; }
        if (this.inDoorway(dr.solid)) { this.doorTimers.set(id, t + 1); continue; }
        this.rt().setDoorOpen(id, false); this.doorTimers.delete(id);
      }
      this.tickAutoDoors(t);
      if (t - this.trailAt > 0.5) { this.trailAt = t; this.updateTrail(); this.updateSigns(); }
      tickRats(this, dt, t);
      this.moveRat(dt, t);

      this.acc.prox += dt; this.acc.save += dt; this.acc.timed += dt; this.acc.workers += dt;
      if (this.acc.prox > 0.4) { this.acc.prox = 0; tickProximity(this); }
      if (this.acc.timed > 1) {
        this.acc.timed = 0; tickTimed(this); tickMissions(this);
        this.watchRunning(t);
        this.watchIntern(t);
        tickVisits(this, () => nextRandom(this.day));
        this.watchHiddenDoor();
        for (const n of patrollers(this)) {
          const here = st.actors.some(a => a.id === `patrol:${n.id}`), should = watcherFloor(this, n) === this.day.floor;
          if (should && !here) this.spawnPatrol(n);
          if (!should && here) { st.removeActors(a => a.id === `patrol:${n.id}`); watcherPos.delete(n.id); }
        }
        this.seeProducts();
      }
      if (this.acc.workers > 2.5) { this.acc.workers = 0; for (const a of st.actors) if (a.kind === 'worker' && a.mode !== 'seated' && Math.random() < 0.18) a.mode = a.mode === 'busy' ? 'wait' : a.mode === 'wait' ? 'busy' : Math.random() < 0.5 ? 'busy' : 'idle'; }
      if (this.acc.save > 2) { this.acc.save = 0; this.day.pos = this.playerPos(); this.save(); }
    }
    this.animate(dt, t, active, inShower);
    st.followCamera(dt);
    st.render();
  }
  /** 現在要去哪裡（給導航小點、小地圖用）：案件 > 任務；目標在別層就先帶去樓梯 */
  goalPoint(): { x: number; z: number; label: string; y?: number } | null {
    if (!this.day) return null;
    const rt = this.rt(); const d = this.day;
    if (!d.clockedIn) { const e = rt.equipment.get('time-clock'); return e ? { x: e.data.pos[0], z: e.data.pos[1], label: tr("打卡鐘") } : null; }
    // v1.1.1 洗手洗到一半（或任務要洗手）：「!」指向下一台（洗手台 → 酒精消毒機 → 烘手機）
    const washGoal = () => {
      const want = WASH_ORDER[Math.min(2, d.washStep ?? 0)]; const [px, pz] = this.playerPos();
      const e = [...rt.equipment.values()].map(q => q.data).filter(q => (q.interaction?.type === 'wash' || q.interaction?.type === 'sink') && washKindOf(q.kind) === want)
        .sort((a, b) => Math.hypot(a.pos[0] - px, a.pos[1] - pz) - Math.hypot(b.pos[0] - px, b.pos[1] - pz))[0];
      return e ? { x: e.pos[0], z: e.pos[1], label: e.name } : null;
    };
    if (d.inProd && !d.washed && (d.washStep ?? 0) > 0) { const w = washGoal(); if (w) return w; }
    let floor: FloorIdT | null = null, id: string | null = null;
    // v1.3「作業在哪裡」→「帶我去」：先帶去那裡（走到旁邊就清掉）
    const wp = d.waypoint;
    if (wp) {
      const e = this.c.floorById.get(wp.floor)?.equipment.find(q => q.id === wp.target);
      const [px, pz] = this.playerPos();
      if (!e) d.waypoint = null;
      else if (wp.floor === d.floor && Math.hypot(e.pos[0] - px, e.pos[1] - pz) < 2.6) { d.waypoint = null; this.ui.toast(tr("📍 到了：{0}", wp.label)); }
      else if (wp.floor === d.floor) return { x: e.pos[0], z: e.pos[1], label: wp.label };
      else { floor = wp.floor; id = wp.target; }
    }
    const k = activeCase(this);
    if (k && d.cases[k.id].stage !== 'done') { floor = k.floor; id = d.cases[k.id].stage === 'inspect' ? k.equipment : `case:${k.id}`; }
    else if (!floor) {
      const cur = currentMission(this);
      if (cur) {
        floor = cur.st.floor; id = cur.st.target;
        // v1.3 要先拿背包物品（烤盤、沙拉油、溫度計……）：先帶去最近的貨架
        const miss = missingNeeds(this, cur.st);
        if (miss.length) {
          const [px, pz] = this.playerPos();
          const src = miss.flatMap(m => packSources(this, m)).sort((a, b) => (a.floor === d.floor ? 0 : 1) - (b.floor === d.floor ? 0 : 1) || Math.hypot(a.x - px, a.z - pz) - Math.hypot(b.x - px, b.z - pz))[0];
          if (src) { if (src.floor === d.floor) return { x: src.x, z: src.z, label: src.label, y: src.y }; floor = src.floor as FloorIdT; }
        }
        // v1.3 線邊溫度紀錄表：還沒看過的溫度計先去看
        if (cur.st.do === 'minigame' && cur.st.params?.useTemps) {
          const [px, pz] = this.playerPos();
          const un = tempSpots(this, cur.st.floor).filter(t => !t.read).sort((a, b) => Math.hypot(a.x - px, a.z - pz) - Math.hypot(b.x - px, b.z - pz))[0];
          if (un && cur.st.floor === d.floor) return { x: un.x, z: un.z, label: un.name };
        }
        // v1.1 要搬一籃料、手上還沒有：先帶去拿（同一層最近的貨架／籃子；這層沒有就帶去有的那一層）
        if (cur.st.do === 'carry' && cur.st.carry && !basketHeldOrOnCart(this, cur.st.carry.content)) {
          const [px, pz] = this.playerPos();
          const src = carrySources(this, cur.st.carry.content).sort((a, b) => (a.floor === d.floor ? 0 : 1) - (b.floor === d.floor ? 0 : 1) || Math.hypot(a.x - px, a.z - pz) - Math.hypot(b.x - px, b.z - pz))[0];
          if (src) { if (src.floor === d.floor) return { x: src.x, z: src.z, label: src.label, y: src.y }; floor = src.floor as FloorIdT; }
        }
      }
    }
    if (!floor || !id) return null;
    if (floor === d.floor && !d.washed && currentMission(this)?.st.do === 'wash' && !k) { const w = washGoal(); if (w) return w; }
    if (floor !== d.floor) {
      const st = [...rt.equipment.values()].map(e => e.data).filter(e => e.interaction?.type === 'stairs' && e.interaction.to.includes(floor!))
        .sort((a, b) => Math.hypot(a.pos[0] - this.playerPos()[0], a.pos[1] - this.playerPos()[1]) - Math.hypot(b.pos[0] - this.playerPos()[0], b.pos[1] - this.playerPos()[1]))[0]
        ?? [...rt.equipment.values()].map(e => e.data).find(e => e.interaction?.type === 'stairs');
      return st ? { x: st.pos[0], z: st.pos[1] + st.size[2] / 2 + 0.6, label: tr("樓梯（往 {0}）", floor) } : null;
    }
    if (id.startsWith('case:') || id.startsWith('npc:')) {
      const a = this.stage.actors.find(x => (id!.startsWith('case:') ? x.caseId === id!.slice(5) : x.npcId === id!.slice(4)));
      return a ? { x: a.group.position.x, z: a.group.position.z, label: '' } : null;
    }
    if (id.startsWith('zone:')) { const z = rt.floor.zones.find(q => q.id === id!.slice(5)); return z ? { x: z.rect[0] + z.rect[2] / 2, z: z.rect[1] + z.rect[3] / 2, label: z.name } : null; }
    const an = rt.anchor(id); return an ? { x: an.x, z: an.z, label: '' } : null;
  }
  /** v1.1 頭上的標示：黃色「!」＝現在任務要去（要拿東西）的地方；綠色「🎮」＝這台機器有作業可以做 */
  private signKey = '';
  updateSigns(): void {
    if (!this.day) return;
    const rt = this.rt(); const out: { x: number; z: number; y: number; kind: 'goal' | 'job' }[] = [];
    const g = this.goalPoint();
    if (g && this.day.clockedIn) {
      let y = g.y ?? 2.4;
      if (g.y == null) { const e = [...rt.equipment.values()].find(q => Math.abs(q.data.pos[0] - g.x) < 0.01 && Math.abs(q.data.pos[1] - g.z) < 0.01); if (e) y = e.data.size[1] + 0.9; }
      out.push({ x: g.x, z: g.z, y, kind: 'goal' });
    }
    const seen = new Set<string>();
    for (const m of this.c.missions) for (const st of m.steps) {
      if (st.do !== 'minigame' || st.floor !== this.day.floor || seen.has(st.target)) continue;
      const pr = this.day.missions[m.id]; if (pr?.done && !(pr.failed && !this.day.retried?.includes(m.id))) continue;
      const e = rt.equipment.get(st.target); if (!e) continue;
      seen.add(st.target); out.push({ x: e.data.pos[0] + e.data.size[0] / 2 - 0.15, z: e.data.pos[1] + e.data.size[2] / 2 - 0.15, y: e.data.size[1] + 0.3, kind: 'job' });
    }
    const key = out.map(o => `${o.kind}${o.x.toFixed(1)},${o.z.toFixed(1)}`).join('|');
    if (key !== this.signKey) { this.signKey = key; this.stage.setSigns(out); }
  }
  private updateTrail(): void {
    if (!this.day || !this.profile.settings.guide) { this.stage.setTrail([]); return; }
    const g = this.goalPoint(); if (!g) { this.stage.setTrail([]); return; }
    const nav = this.stage.nav; const goal = nav.nearestOpen(g.x, g.z); if (!goal) { this.stage.setTrail([]); return; }
    const path = nav.find(this.playerPos(), goal, true) ?? [];
    this.stage.setTrail(path.filter((_, i) => i % 2 === 1).slice(0, 40));
  }
  /** v1.0 自由模式：實習生跑出自己的區域，被主管抓到 → 被罵爛 */
  private internWarnAt = -99;
  private watchIntern(t: number): void {
    if (this.day.mode !== 'intern' || t - this.internWarnAt < 25) return;
    const [x, z] = this.playerPos(); const zn = this.rt().zoneAt(x, z); if (!zn) return;
    if (internAllowed(this, this.day.floor, zn.level)) return;
    const boss = supervisorNear(this, x, z); if (!boss) return;
    this.internWarnAt = t; bump(this, 'internCaught');
    this.apply({ trust: -6 }, 'intern'); this.day.player.stress = Math.min(100, this.day.player.stress + 15);
    this.keys.clear(); this.walkTo = null; this.path = [];
    const role = this.c.roleById.get(this.day.roleId)!;
    this.log(tr("實習生跑到{0}，被{1}抓到", zn.name, boss.name), 'bad');
    this.ui.card({ name: boss.name, role: boss.title, color: boss.color, npc: boss.id, expression: 'angry', badge: { text: tr("😡 被罵爛了"), color: '#d9534a' },
      text: tr("你是哪個部門的實習生？{0}的人跑來{1}幹嘛？！沒有人帶你不准亂走！生產區不是參觀的地方，東西碰壞了、帶了髒東西進來誰負責？馬上回你們{2}！", role.deptName, zn.name, role.startFloor),
      sections: [{ label: tr("實習生規定"), items: [tr("只能待在自己部門的樓層（{0}）和非生產區（更衣室、走廊、辦公室、餐廳）。", role.startFloor), tr("想去別的區域：先找那一區的主管同意，有人帶才可以。")], tone: 'warn' }],
      choices: [{ text: tr("對不起！我馬上回去"), tone: 'primary', fn: () => this.ui.close() }] });
  }
  /** v1.1 4F 暗門開著沒恢復原狀，被品保看到 → 記錄、罰款（品保順手把它關回去） */
  private watchHiddenDoor(): void {
    const d = this.day; if (!d.flags.hiddenOpen || d.floor !== '4F' || this.ui.isOpen()) return;
    const door = this.rt().doors.get('door-4f-hidden'); if (!door) return;
    const w = whoSees(this, door.data.pos[0], door.data.pos[1]); if (!w || !QA_WATCHERS.has(w.id)) return;
    const fine = this.c.game.hygiene.finePerViolation;
    d.flags.hiddenOpen = false; d.factory.caught++; d.factory.fines += fine; bump(this, 'caught');
    this.apply({ trust: -4, records: -2 }, 'hidden-door');
    this.log(tr("4F 暗門開著沒恢復原狀，被{0}看到（記錄、罰款 ${1}）", w.name, fine), 'bad');
    bus.emit('sfx', 'warn');
    this.ui.card({ name: w.name, role: w.title, color: w.color, npc: w.id, expression: 'angry', badge: { text: tr("📝 記錄・罰款 ${0}", fine), color: '#d9534a' },
      text: tr("這面牆怎麼是開著的？誰拉開的沒有恢復原狀？這種地方開著，灰塵、蟲都會跑進來。我先記一筆，罰款 {0} 元。下次用完一定要拉回去！", fine),
      choices: [{ text: tr("對不起，下次一定恢復原狀"), tone: 'primary', fn: () => { this.ui.close(); this.rebuildFloor(); } }] });
    this.save();
  }
  /** 在生產區跑步，被品保／駐廠看到 → 警告 */
  private watchRunning(t: number): void {
    if (!this.isRunning || !this.walking) return;
    const z = this.rt().zoneAt(...this.playerPos()); if (!z || z.level < 0) return;
    const w = whoSees(this); if (!w || !QA_WATCHERS.has(w.id) || t - this.runWarnAt < 20) return;
    this.runWarnAt = t; bump(this, 'runWarned');
    this.apply({ trust: -this.c.game.move.runWarnTrust }, 'run');
    this.log(tr("在{0}跑步，被{1}警告", z.name, w.name), 'warn');
    this.runToggle = false; this.shift = false; this.keys.clear();
    this.ui.card({ name: w.name, role: w.title, color: w.color, npc: w.id, expression: 'angry', badge: { text: tr("⚠ 警告：不能跑"), color: '#e07b39' },
      text: tr("欸！生產區不能用跑的！地板濕、轉角有推車，撞到人或滑倒都會出事。"),
      sections: [{ label: tr("廠規"), items: [tr("生產區用走的。趕時間也一樣。")] }],
      choices: [{ text: tr("對不起，我用走的"), tone: 'primary', fn: () => this.ui.close() }] });
  }
  /** 💬 和作業員聊兩句：打招呼＋一則小知識（依所在區域、部門） */
  private chatWorker(actorId: string): void {
    const i = Number(actorId.slice(1)); const w = this.rt().floor.workers[i]; if (!w) return;
    const z = this.rt().zoneAt(w.pos[0], w.pos[1]);
    const who = w.uniform === 'office' ? tr("辦公室同事") : w.uniform === 'green' ? tr("倉儲部同事") : w.uniform === 'gray' ? tr("工務部同事") : tr("生產部作業員");
    const hat = w.hat === 'yellow' ? tr("（新人）") : w.hat === 'pink' ? tr("（孕婦）") : w.hat === 'blue' ? tr("（組長）") : w.hat?.startsWith('blue') ? tr("（主管）") : '';
    const first = !this.day.chatted.includes(actorId);
    if (first) { this.day.chatted.push(actorId); gainXp(this, 'chat'); bump(this, 'chats'); }
    const hello = w.mode === 'busy' ? [tr("手上在忙，邊做邊跟你說："), tr("欸你好！我先把這批做完……"), tr("等我一下下——好，什麼事？")] : w.mode === 'seated' ? [tr("你好～找誰？"), tr("我在打報表，你說。")] : [tr("嗨！今天還順利嗎？"), tr("新來的嗎？跟你說一個小秘訣："), tr("休息一下下，聊兩句。")];
    this.tipCard(`${w.tag ?? who}${hat}`, z?.name ?? '', '#7c8f9c', undefined, hello[i % hello.length], w.uniform);
  }
  /** 小知識卡：依玩家所在區域挑沒聽過的 */
  tipCard(name: string, role: string, color: string, npc?: string, hello = tr("跟你說一個工廠的小知識："), uniform?: string): void {
    const zid = this.rt().zoneAt(...this.playerPos())?.id ?? '';
    const whoTag = uniform === 'office' ? 'office' : uniform === 'green' ? 'green' : uniform === 'gray' ? 'gray' : 'worker';
    const fits = this.c.tips.filter(t => t.where.some(w => w === 'any' || zid.startsWith(w)) && (!t.who || !uniform || t.who === whoTag));
    const local = fits.filter(t => !t.where.includes('any'));
    const fresh = [...local, ...fits].filter(t => !this.profile.tipsHeard.includes(t.id));
    const pool = fresh.length ? fresh : fits.length ? fits : this.c.tips;
    const freshLocal = local.filter(t => !this.profile.tipsHeard.includes(t.id));
    const pick = freshLocal.length ? freshLocal : pool;
    const tip = pick[Math.floor(nextRandom(this.day) * pick.length)];
    const isNew = !this.profile.tipsHeard.includes(tip.id);
    if (isNew) { this.profile.tipsHeard.push(tip.id); check(this); }
    this.ui.card({ name, role, color, npc, text: `${hello}\n\n💡 ${tip.text}`,
      sections: isNew ? [{ label: tr("📚 小知識收集"), items: [tr("{0}／{1}（手冊「小知識」可以重看）", this.profile.tipsHeard.length, this.c.tips.length)], tone: 'good' }] : undefined,
      choices: [{ text: tr("謝謝，學到了！"), tone: 'primary', fn: () => this.ui.close() }] });
  }
  /** 3F 醫務室：辦公桌（廠醫每週三 14:00～16:00）、單人床、體重計 */
  clinicCard(id: string): void {
    const d = this.day, P = d.player;
    const wd = weekdayOf(d.day);
    const doc = this.c.npcs.find(n => n.id === 'factory-doctor');
    const docHere = !!doc && this.npcOnDuty(doc);
    if (id === 'clinic-bed') {
      this.ui.card({ name: tr("🛏️ 醫務室單人床"), role: tr("3F 醫務室"), color: '#9fd3f5', text: tr("不舒服可以躺 20 分鐘。真的受傷一定要通報主管、寫事故紀錄。"),
        sections: [{ label: tr("躺 20 分鐘"), items: [tr("體力 +20"), tr("疲勞 −12"), tr("壓力 −5")], tone: 'good' }],
        choices: [{ text: tr("躺一下"), tone: 'primary', fn: () => { this.advance(20, tr("醫務室休息")); P.hp = clamp(P.hp + 20); P.fatigue = clamp(P.fatigue - 12); P.stress = clamp(P.stress - 5); this.ui.close(); this.ui.toast(tr("😌 好多了。")); this.save(); } },
          { text: tr("不用了"), fn: () => this.ui.close() }] });
      return;
    }
    if (id === 'clinic-scale') {
      const kg = 48 + Math.floor(nextRandom(d) * 30);
      this.ui.card({ name: tr("⚖️ 體重計"), role: tr("3F 醫務室"), color: '#9fd3f5', text: tr("站上去……{0}.{1} 公斤。\n穿著雨鞋和工作服，大概要扣掉 1.5 公斤。記得多喝水、按時吃飯！", kg, Math.floor(nextRandom(d) * 10)), choices: [{ text: tr("好"), fn: () => this.ui.close() }] });
      return;
    }
    if (id === 'doctor' || docHere) {
      const light = d.injuries.filter(i => i.severity === 'light').length;
      this.ui.card({ name: doc?.name ?? tr("廠醫"), role: tr("廠醫（每週三 14:00～16:00）"), color: doc?.color ?? '#5fb38a', npc: doc?.id, expression: 'happy',
        text: light ? tr("來，我看看傷口。清潔、消毒、包紮好了。今天別再勉強，有事馬上跟主管說。") : tr("你氣色不錯！最近睡得好嗎？疲勞 {0}——{1}", Math.round(P.fatigue), P.fatigue > 50 ? tr("有點累，下班早點休息。") : tr("保持下去。")),
        sections: [{ label: tr("廠醫的建議"), items: [tr("每 1～2 小時喝水、上廁所"), tr("搬重物用推車，不要硬抬"), tr("手上有傷口一定要包好、戴手套")], tone: 'info' }],
        choices: [{ text: tr("謝謝醫師"), tone: 'primary', fn: () => { P.hp = clamp(P.hp + (light ? 25 : 10)); P.stress = clamp(P.stress - 8); this.advance(10, tr("看廠醫")); bump(this, 'doctorVisits'); this.ui.close(); this.save(); } }] });
      return;
    }
    this.ui.card({ name: tr("🩺 醫務室"), role: tr("3F・醫務室"), color: '#9fd3f5',
      text: tr("廠醫每週三 14:00～16:00 來 2 小時（今天是星期{0}）。\n平常這裡有急救箱、單人床和體重計。受傷一定要通報主管。", WEEKDAY[wd - 1]),
      choices: [{ text: tr("🩹 用急救箱處理小傷口"), fn: () => { this.advance(5, tr("急救")); P.hp = clamp(P.hp + 8); this.ui.close(); this.ui.toast(tr("🩹 消毒、貼好 OK 繃。記得戴手套。")); } }, { text: tr("好"), fn: () => this.ui.close() }] });
  }

  private seeProducts(): void {
    const [x, z] = this.playerPos();
    for (const cv of this.rt().conveyors) {
      if (!cv.line) continue;
      // v0.6：站在產線旁邊（4 公尺內），就能記下現在在包的成品（食譜收集）
      const ln = this.day.lines[cv.line];
      if (ln && Math.hypot(cv.eq.pos[0] - x, cv.eq.pos[1] - z) < 4 && this.day.clock >= ln.changeoverUntil && !ln.stoppedBy) {
        const pair = menuToday(this, cv.line); if (pair) unlockRecipe(this, ln.alt ? pair[1] : pair[0], tr("在 {0} 產線旁看到", this.day.floor));
      }
      if (Math.hypot(cv.eq.pos[0] - x, cv.eq.pos[1] - z) > 9) continue;
      if (!this.profile.productsSeen.includes(cv.line)) { this.profile.productsSeen.push(cv.line); this.ui.toast(tr("🍱 成品圖鑑：{0}", this.c.productById.get(cv.line)?.name)); check(this); }
    }
  }
  private ratTarget: [number, number] | null = null; private ratRetarget = 0;
  /** 老鼠亂跑：每 1.5 秒換方向；玩家靠近（2.5 m 內）就往反方向跑 */
  private moveRat(dt: number, t: number): void {
    const r = this.day.rat; if (!r || r.floor !== this.day.floor) return;
    const [px, pz] = this.playerPos();
    const near = Math.hypot(r.pos[0] - px, r.pos[1] - pz) < 2.5;
    if (!this.ratTarget || t > this.ratRetarget) {
      const z = this.rt().floor.zones.find(q => q.id === r.zone);
      if (near) { const dx = r.pos[0] - px, dz = r.pos[1] - pz, l = Math.hypot(dx, dz) || 1; this.ratTarget = [r.pos[0] + dx / l * 2.5, r.pos[1] + dz / l * 2.5]; }
      else this.ratTarget = z ? this.randomOpenIn(z.rect as [number, number, number, number]) : null;
      this.ratRetarget = t + (near ? 0.8 : 1.6);
    }
    if (!this.ratTarget) return;
    const vx = this.ratTarget[0] - r.pos[0], vz = this.ratTarget[1] - r.pos[1], l = Math.hypot(vx, vz);
    if (l < 0.05) return;
    const sp = (near ? 2.6 : 1.1) * dt;
    const nx = r.pos[0] + vx / l * Math.min(sp, l), nz = r.pos[1] + vz / l * Math.min(sp, l);
    if (this.rt().walkable(nx, nz, 0.15)) r.pos = [nx, nz]; else this.ratTarget = null;
    this.stage.moveRat(r.pos, Math.atan2(vx, vz));
  }
  private arrive() { if (this.pending) { const n = this.nearest(); if (n && n.id === this.pending.id) this.interact(n); } this.pending = null; }

  private animate(dt: number, t: number, active: boolean, inShower: boolean): void {
    const st = this.stage, p = st.player.position;
    animateChibi(st.player, t, inShower ? 'idle' : this.walking ? (this.day?.pushing ? 'push' : this.day?.holding ? 'carry' : this.isRunning ? 'run' : 'walk') : 'idle', this.reduceMotion);
    animateDoors(st.rt, dt);
    if (inShower) { st.player.rotation.y += dt * 4; }
    st.updateEffects(t, inShower);
    const visionPts: [number, number][] = [];
    for (const a of st.actors) {
      if (a.kind === 'patrol') { if (active) this.movePatrol(a, dt); visionPts.push([a.group.position.x, a.group.position.z]); }
      else if (a.kind === 'ambient' && a.npcId && this.c.npcById.get(a.npcId)?.route) { if (active) this.moveRoute(a, dt, t); }
      else if (a.kind !== 'worker' && Math.hypot(p.x - a.group.position.x, p.z - a.group.position.z) < 4) {
        const want = Math.atan2(p.x - a.group.position.x, p.z - a.group.position.z);
        let diff = want - a.group.rotation.y; diff = Math.atan2(Math.sin(diff), Math.cos(diff));
        a.group.rotation.y += diff * Math.min(1, dt * 5);
      }
      if (a.label) a.label.position.set(a.group.position.x, 2.3, a.group.position.z);
      st.animateActor(a, t, this.reduceMotion);
    }
    st.setVision(visionPts, this.c.game.watchers.visionRadius);
    if (!this.day) { st.render(); return; }
    // 推車跟著玩家
    st.syncCarts(this.day.carts, this.day.floor, this.day.pushing, this.basketColor, this.day.holding?.kind === 'cart' ? this.day.holding.id : null);
    st.syncBaskets(this.day.baskets, this.day.floor, this.contentColor);
    if (this.day.pushing) { const g = st.carts.get(this.day.pushing); const c = this.day.carts[this.day.pushing]; if (g) { g.position.set(c.pos[0], 0, c.pos[1]); g.rotation.y = st.player.rotation.y + Math.PI / 2; } }
    st.syncPickups(this.day.ground, this.day.floor, tt => { const q = this.c.pickupById.get(tt)!; return { emoji: q.icon, image: q.image }; });
    for (const s of st.pickups.values()) s.position.y = 0.4 + (this.reduceMotion ? 0 : Math.sin(t * 3 + s.position.x) * 0.06);
    st.animateConveyors(t, line => {
      if (!line) return { product: null, alt: false, stopped: false };
      const ln = this.day.lines[line];
      return { product: this.c.productById.get(line) ?? null, alt: !!ln?.alt, stopped: !!ln && (!!ln.stoppedBy || this.day.clock < ln.changeoverUntil || this.day.clock < ln.reworkUntil) };
    });
    // 任務標記
    const k = activeCase(this);
    const bob = this.reduceMotion ? 0 : Math.sin(t * 3) * 0.08;
    st.marker.visible = false; st.targetRing.visible = false;
    let markerSet = false;
    if (k && k.floor === this.day.floor) {
      const stg = this.day.cases[k.id].stage;
      if (stg === 'inspect') {
        const e = this.rt().equipment.get(k.equipment);
        if (e) { st.marker.visible = true; st.marker.position.set(e.data.pos[0], Math.max(e.data.size[1], 1.2) + 1.2 + bob, e.data.pos[1]); markerSet = true;
          st.targetRing.visible = true; st.targetRing.position.set(e.data.pos[0], 0.03, e.data.pos[1] + e.data.size[2] / 2 + 0.6); }
      } else {
        const a = st.actors.find(x => x.caseId === k.id);
        if (a) { st.marker.visible = true; st.marker.position.set(a.group.position.x, 2.95 + bob, a.group.position.z); markerSet = true; }
      }
    }
    if (!markerSet && !k) {
      const cur = currentMission(this);
      if (cur && cur.st.floor === this.day.floor) {
        const tg = cur.st.target;
        if (tg.startsWith('npc:')) { const a = st.actors.find(x => x.npcId === tg.slice(4)); if (a) { st.marker.visible = true; st.marker.position.set(a.group.position.x, 2.95 + bob, a.group.position.z); } }
        else { const an = this.rt().anchor(tg.startsWith('zone:') ? '' : tg); if (an) { st.marker.visible = true; st.marker.position.set(an.x, 2.6 + bob, an.z); st.targetRing.visible = true; st.targetRing.position.set(an.x, 0.03, an.box.maxZ + 0.6); } }
      }
    }
    // v1.2 手機畫面小：只顯示比較近的名牌，免得字疊在一起
    const touch = document.body.classList.contains('touch'), R1 = touch ? 4.2 : 6.5, R2 = touch ? 7.5 : 14;
    for (const e of this.rt().equipment.values()) {
      const d = Math.hypot(e.data.pos[0] - p.x, e.data.pos[1] - p.z);
      e.label.visible = !st.dark && (d < R1 || (!!e.data.interaction && d < R2) || (k?.equipment === e.data.id));
    }
    const ring = st.tapRing.material as THREE.MeshBasicMaterial; if (ring.opacity > 0) ring.opacity = Math.max(0, ring.opacity - dt * 2);
    if (k && this.day.cases[k.id].stage !== 'done') {
      const e = this.rt().equipment.get(k.equipment);
      if (e?.screen) (e.screen.material as THREE.MeshLambertMaterial).emissive.setHex(Math.sin(t * 5) > 0 ? 0xff4f42 : 0x5a1a16);
    }
  }
  /** v1.1 照路線來回走的 NPC（送菜的廠商）；兩端各停一下 */
  // v1.1.1 用「角色物件」當 key：換樓層回來角色會重建在起點，舊的進度若沿用會從起點直線走到半路的目標點而穿牆
  private routeState = new WeakMap<Actor, { i: number; dir: 1 | -1; waitUntil: number }>();
  private moveRoute(a: Actor, dt: number, t: number): void {
    const route = this.c.npcById.get(a.npcId!)!.route!;
    const s = this.routeState.get(a) ?? { i: 0, dir: 1 as const, waitUntil: 0 };
    this.routeState.set(a, s);
    if (t < s.waitUntil) { a.mode = 'idle'; return; }
    const [tx, tz] = route[s.i];
    const vx = tx - a.group.position.x, vz = tz - a.group.position.z, d = Math.hypot(vx, vz), sp = 1.1 * dt;
    // 自動門還沒開（另一道還開著）就在門前等
    const blockedDoor = [...this.rt().doors.values()].find(r => r.data.auto && !r.open && Math.hypot(r.data.pos[0] - a.group.position.x, r.data.pos[1] - a.group.position.z) < 1.1 && Math.hypot(r.data.pos[0] - tx, r.data.pos[1] - tz) < Math.hypot(r.data.pos[0] - a.group.position.x, r.data.pos[1] - a.group.position.z) + d - 0.2);
    if (blockedDoor) { a.mode = 'idle'; return; }
    a.mode = 'walk';
    if (d < sp) {
      a.group.position.set(tx, 0, tz);
      const next = s.i + s.dir;
      if (next < 0 || next >= route.length) { s.dir = s.dir === 1 ? -1 : 1; s.waitUntil = t + 5; s.i += s.dir; }
      else s.i = next;
    } else { a.group.position.x += vx / d * sp; a.group.position.z += vz / d * sp; a.group.rotation.y = Math.atan2(vx, vz); }
  }
  /** v1.1 自動門：人（玩家或走路線的 NPC）靠近就開、走遠就關；同一組 interlock 一次只能開一道 */
  private autoDoorMsgAt = 0;
  private tickAutoDoors(t: number): void {
    const rt = this.rt(); const [px, pz] = this.playerPos();
    const walkers: [number, number][] = [[px, pz], ...this.stage.actors.filter(a => a.kind === 'ambient' && a.npcId && this.c.npcById.get(a.npcId)?.route).map(a => [a.group.position.x, a.group.position.z] as [number, number])];
    for (const r of rt.doors.values()) {
      if (!r.data.auto || !r.solid) continue;
      const near = walkers.some(([x, z]) => Math.hypot(x - r.data.pos[0], z - r.data.pos[1]) < 1.7);
      const partnerOpen = [...rt.doors.values()].some(o => o !== r && o.data.interlock && o.data.interlock === r.data.interlock && o.open);
      if (near && !r.open) {
        if (partnerOpen) {
          if (Math.hypot(px - r.data.pos[0], pz - r.data.pos[1]) < 1.7 && t - this.autoDoorMsgAt > 3) { this.autoDoorMsgAt = t; this.ui.toast(tr("🚪 另一邊的自動門還開著，等它關上，這道門才會打開（防止蚊蟲、灰塵直接吹進生產區）。")); }
          continue;
        }
        rt.setDoorOpen(r.data.id, true); bus.emit('sfx', 'door');
      } else if (!near && r.open) {
        const insideDoorway = walkers.some(([x, z]) => x > r.solid!.minX - 0.45 && x < r.solid!.maxX + 0.45 && z > r.solid!.minZ - 0.45 && z < r.solid!.maxZ + 0.45);
        if (!insideDoorway) rt.setDoorOpen(r.data.id, false);
      }
    }
  }
  private movePatrol(a: Actor, dt: number): void {
    const st = this.stage;
    if (!a.path || !a.path.length) {
      const zones = this.rt().floor.zones.filter(z => z.level >= 0 && !z.cold);
      const z = zones.length ? zones[Math.floor(Math.random() * zones.length)] : this.rt().floor.zones[0];
      const goal = st.nav.randomOpenIn(z.rect, Math.random);
      a.path = goal ? st.nav.find([a.group.position.x, a.group.position.z], goal) ?? [] : [];
      if (!a.path.length) { a.mode = 'idle'; return; }
    }
    const [tx, tz] = a.path[0];
    const vx = tx - a.group.position.x, vz = tz - a.group.position.z, d = Math.hypot(vx, vz);
    const sp = (a.speed ?? 1.2) * dt;
    a.mode = 'walk';
    if (d < sp) { a.group.position.set(tx, 0, tz); a.path.shift(); }
    else { a.group.position.x += vx / d * sp; a.group.position.z += vz / d * sp; a.group.rotation.y = Math.atan2(vx, vz); }
    const id = a.npcId!;
    watcherPos.set(id, { x: a.group.position.x, z: a.group.position.z, floor: this.day.floor });
    const gg = this.day.items.guaiguai;
    if (gg && gg.floor === this.day.floor && !this.day.items.spotted && !this.ui.isOpen() && this.c.npcById.get(id)?.patrol?.watches) {
      const e = this.rt().equipment.get(gg.equipment);
      if (e && distToRect(a.group.position.x, a.group.position.z, e.box) < this.c.game.watchers.guaiguaiSpotRadius) auditorSpotsGuaiguai(this, id);
    }
  }
}

const DEPT_NAME: Record<string, string> = { warehouse: tr("倉儲發料"), thaw: tr("解凍"), ship: tr("成品出貨"), prep: tr("前處理"), rice: tr("煮飯・拌飯"), cook: tr("調理"), slice: tr("熟切"), pack: tr("包裝"), qa: tr("品保"), rd: 'RD', purchasing: tr("採購"), planning: tr("生管"), engineering: tr("工務"), facility: tr("廠務"), hr: tr("人資") };
export { DEPT_NAME };
